// src/pages/admin/AnalyseTurpe.jsx - VERSION REDESIGNÉE
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useOutletContext, useParams, useLocation, Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import PDFExporter from '../../components/PDFExporter';
import SmartOptimizationPanel from '../../components/optimization/SmartOptimizationPanel';
import ManualOptimizationPanel from '../../components/optimization/ManualOptimizationPanel';
import DepassementsParMois from '../../components/DepassementsParMois';
import VersionUtilisationOptimizer from '../../components/optimization/VersionUtilisationOptimizer';
import PDLProgressBadge from '../../components/dashboard/PDLProgressBadge';
import EnedisDegradedBanner from '../../components/dashboard/EnedisDegradedBanner';
import { useOptimizablePDLs } from '../../hooks/dashboard/useOptimizablePDLs';
import { resolveTensionDomain, getCmdpsRule } from '../../utils/cmdpsRule';
import { lireReponseApi } from '../../utils/reponseApiTurpe';
import { relancerAnalyseTurpe } from '../../utils/relancerAnalyseTurpe';
import { economieVersionDuResume, estConcluante, motifBlocage as motifBlocageDe } from '../../utils/resumeTurpe';
import BandeauConclusionTurpe from '../../components/turpe/BandeauConclusionTurpe';
import {
  CocheAnalyse, CellulesOptimisation, BoutonRapportTurpe, choisirResume, totauxCompte, euros,
} from '../../components/turpe/ResumeAnalyseTurpe';
import {
  FiArrowLeft, FiZap, FiTrendingUp, FiAlertTriangle,
  FiTarget, FiBarChart2, FiRefreshCw, FiLoader, FiUser,
  FiCheckCircle, FiEye, FiActivity, FiInfo,
  FiPieChart, FiTool, FiChevronDown, FiChevronRight, FiFileText, FiSearch,
  FiFilter, FiChevronUp, FiPlay,
  FiShield, FiAlertCircle, FiXCircle, FiMail,
  FiMoreVertical, FiDownload, FiX, FiEdit2,
  FiPaperclip, FiUploadCloud, FiFile, FiCornerUpLeft
} from 'react-icons/fi';
import { FaEuroSign } from 'react-icons/fa';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';

const ClientAccordion = ({ client, allAnalyses, selectedYear }) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm transition-all duration-300 hover:shadow-md">
      {/* Header cliquable de l'accordéon */}
      <button 
        onClick={() => setIsOpen(!isOpen)} 
        className="w-full flex items-center justify-between p-3 text-left"
      >
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
            <FiUser className="h-4 w-4 text-white" />
          </div>
          <div>
            <h3 className="text-xs font-semibold text-gray-900">{client.user_name}</h3>
            <p className="text-xs text-gray-600">{client.company_name}</p>
          </div>
        </div>
        <div className="flex items-center space-x-4">
          <div className="text-right">
            <p className="text-xs text-gray-500">PDL analysés</p>
            <p className="text-xs font-bold text-blue-600">{client.analyzedPdlCount}/{client.totalPdlCount}</p>
          </div>
          <FiChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {/* Contenu qui s'affiche quand l'accordéon est ouvert */}
      {isOpen && (
        <div className="border-t border-gray-200 p-3 bg-gray-50">
          <div className="space-y-2">
            {client.pdls.map(pdl => {
              const analysis = allAnalyses.find(a => a.pdl_id === pdl.pdl_id);
              return (
                <div key={pdl.pdl_id} className="flex items-center justify-between p-2 bg-white rounded-lg border hover:bg-gray-50">
                  <div>
                    <p className="text-xs font-medium text-gray-800">{pdl.site_name}</p>
                    <p className="text-xs text-gray-500 font-mono">{pdl.pdl_number}</p>
                  </div>

                  {pdl.hasAnalysis ? (
                    // Cas 1 : L'analyse existe
                      <button
                      onClick={() => navigate(
                        `/dashboard/admin/analyse-turpe/${pdl.pdl_id}`, 
                        { 
                          state: { 
                            pdl: pdl, 
                            analysisResultsByPdl: analysis.analysis_results,
                            year: selectedYear // On ajoute l'année au state
                          } 
                        }
                      )}
                      className="flex items-center space-x-2 px-3 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full hover:bg-blue-200"
                    >
                      <FiEye className="h-4 w-4" />
                      <span>Voir détail</span>
                    </button>
                  ) : (
                    // Cas 2 : Aucune analyse
                    <button
                      onClick={() => navigate(
                      `/dashboard/admin/client/${client.user_id}`, 
                      { state: { defaultTab: 'acheminement' } } // On passe un état
                    )}
                      className="flex items-center space-x-2 px-3 py-1 bg-orange-100 text-orange-800 text-xs font-semibold rounded-full hover:bg-orange-200"
                    >
                      <FiFileText className="h-4 w-4" />
                      <span>Compléter</span>
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ===== SYSTÈME D'ONGLETS =====
const TabSystem = ({ activeTab, onTabChange, tabs }) => {
  return (
    <div className="border-b border-gray-200 bg-white">
      <nav className="flex gap-1 px-2" aria-label="Tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={`${
              activeTab === tab.id
                ? 'border-blue-500 text-blue-600 bg-blue-50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } group inline-flex items-center py-1.5 px-2 border-b-2 font-medium text-[11px] transition-all duration-200`}
          >
            <tab.icon
              className={`${
                activeTab === tab.id ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'
              } mr-1 h-3 w-3`}
            />
            {tab.label}
            {tab.badge && (
              <span className="ml-1.5 bg-red-100 text-red-600 text-[9px] px-1.5 py-0 rounded-full">
                {tab.badge}
              </span>
            )}
          </button>
        ))}
      </nav>
    </div>
  );
};

// ===== COMPOSANT HEADER COMPACT =====
const CompactHeader = ({ pdlData, analysisData, loading, onRefresh, analysisYear, motifBlocage = null }) => {
  return (
    <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white px-3 py-1.5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Link
            to="/dashboard/admin/analyse-turpe"
            className="p-1 hover:bg-white/10 rounded transition-colors flex-shrink-0"
          >
            <FiArrowLeft className="h-3.5 w-3.5" />
          </Link>
          <h1 className="text-xs font-bold truncate flex-shrink-0">
            {pdlData?.site_name || 'Analyse TURPE'}
          </h1>
          <div className="flex items-center gap-2 text-[10px] opacity-90 min-w-0">
            <span className="font-mono bg-white/10 px-1.5 py-0.5 rounded">
              {pdlData?.pdl_number || 'PDL'}
            </span>
            <span className="opacity-60">•</span>
            <span>TURPE 6</span>
            {analysisYear && <><span className="opacity-60">•</span><span>{analysisYear}</span></>}
          </div>
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            onClick={onRefresh}
            disabled={loading}
            title="Recharger l'affichage (ne relance PAS le calcul : utiliser « Relancer l'analyse »)"
            className="p-1 hover:bg-white/10 rounded transition-colors disabled:opacity-50"
          >
            <FiRefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          {analysisData?.analysisResults && (
            <PDFExporter
              analysisData={analysisData}
              analysisSummary={analysisData?.analysisSummary}
              className="bg-white/10 hover:bg-white/20 text-white border-white/20"
              buttonText="PDF"
              motifBlocage={motifBlocage}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// ===== COMPOSANT KPI CARDS COMPACT =====
// Calcule l'éco totale cohérente avec le choix final retenu :
//   - si chosen_optimization → éco PS du choix + éco changement de version (si applicable)
//   - sinon : meilleur des analyses individuelles (max auto/manual/version)
//   - sinon : fallback economie_potentielle_max (en BDD, snapshot analyse auto)
const computeTotalSavings = (analysisSummary, fallback = 0) => {
  if (!analysisSummary) return fallback;
  // Résumé partagé (24/10/2026) : seule source du total ; null = non concluant / non mesuré.
  if (analysisSummary.resume) return analysisSummary.resume.economie_totale_eur;
  const getSaving = (type) => {
    if (type === 'auto')    return Number(analysisSummary.best_saving_eur) || 0;
    if (type === 'manual')  return Number(analysisSummary.manual_saving_eur) || 0;
    if (type === 'version') return Number(economieVersionDuResume(analysisSummary)) || 0;
    return 0;
  };
  if (analysisSummary.chosen_optimization) {
    const psEco = getSaving(analysisSummary.chosen_optimization);
    const couts = analysisSummary.version_of_use_details_json?.couts_par_version
      || analysisSummary.version_of_use_costs
      || null;
    const currentVoU = analysisSummary.version_of_use_details_json?.current_version
      || analysisSummary.current_version_of_use;
    const chosenVoU = analysisSummary.chosen_version_of_use;
    let vouEco = 0;
    if (couts && currentVoU && chosenVoU && currentVoU !== chosenVoU) {
      vouEco = (Number(couts[currentVoU]) || 0) - (Number(couts[chosenVoU]) || 0);
    }
    return psEco + (vouEco > 0 ? vouEco : 0);
  }
  const max = Math.max(getSaving('auto'), getSaving('manual'), getSaving('version'));
  return max > 0 ? max : fallback;
};

const CompactKPICards = ({ results, analysisSummary }) => {
  const turpe6 = results?.turpe6_details || {};
  const economieBdd = turpe6.economie_potentielle_max || turpe6.economie_potentielle || 0;
  // Le KPI "Économie potentielle" doit refléter le choix final retenu, sinon
  // l'utilisateur voit un montant figé sur l'analyse auto initiale (3157 €)
  // alors qu'il a validé manual + CU (= 3025 €).
  const economieAffichee = computeTotalSavings(analysisSummary, economieBdd);

  const kpis = [
    {
      label: 'Économie potentielle',
      value: economieAffichee,
      format: 'currency',
      color: 'emerald',
      icon: FiTrendingUp,
    },
    {
      label: 'Coût total annuel',
      value: turpe6.cout_total_annuel || 0,
      format: 'currency',
      color: 'orange',
      icon: FaEuroSign
    },
    {
      label: 'Dépassements',
      value: turpe6.depassements_detectes?.length || 0,
      format: 'number',
      suffix: 'mois',
      color: 'red',
      icon: FiAlertTriangle
    },
    {
      label: 'CMDPS annuel',
      value: turpe6.composante_depassement || 0,
      format: 'currency',
      color: 'purple',
      icon: FiZap
    }
  ];

  const formatValue = (value, format, suffix = '') => {
    // null = non concluant / non mesuré : jamais affiché comme 0 €.
    if (value === null || value === undefined) return '—';
    if (format === 'currency') {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    }
    return `${value} ${suffix}`;
  };

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-1.5 px-3 py-1.5 bg-gray-50 border-b border-gray-200">
      {kpis.map((kpi, index) => (
        <div key={index} className="bg-white rounded px-2 py-1 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-[9px] text-gray-500 truncate uppercase tracking-wide">{kpi.label}</p>
              <p className={`text-xs font-bold text-${kpi.color}-600 truncate leading-tight`}>
                {formatValue(kpi.value, kpi.format, kpi.suffix)}
              </p>
            </div>
            <div className={`w-5 h-5 bg-${kpi.color}-100 rounded flex items-center justify-center flex-shrink-0`}>
              <kpi.icon className={`h-3 w-3 text-${kpi.color}-600`} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ===== ONGLET SYNTHÈSE =====
const SyntheseTab = ({ results, pdlData, year }) => {
  const turpe6 = results?.turpe6_details || {};
  const turpe7 = results?.turpe7_details || {};
  // Les PS sont dans composantes_detail.CS_FIXE.parametres (1 valeur par cadran).
  const psParams = turpe6.composantes_detail?.CS_FIXE?.parametres || {};
  const subscribedPower = psParams.ps_hph_kw ?? psParams.ps_hch_kw ?? psParams.ps_hpb_kw ?? psParams.ps_hcb_kw ?? null;
  const psByCadran = {
    HPH: psParams.ps_hph_kw ?? subscribedPower,
    HCH: psParams.ps_hch_kw ?? subscribedPower,
    HPB: psParams.ps_hpb_kw ?? subscribedPower,
    HCB: psParams.ps_hcb_kw ?? subscribedPower,
  };

  const analytics = turpe6.details_calcul?.analytics || {};
  // Préférer l'option tarifaire ENEDIS (BTSUPCU4, BTSUPLU4, ...) si dispo via C68,
  // sinon fallback sur le domaine de tension côté moteur TURPE (BT_SUP_36, BTSUP, ...).
  const enedisInfos = Array.isArray(pdlData?.enedis_infos) ? pdlData?.enedis_infos[0] : pdlData?.enedis_infos;
  // Formule : à défaut de C68, la version d'utilisation retenue par le moteur (CU/MU/LU).
  const optionTarifaire = enedisInfos?.option_tarifaire_turpe || analytics.utilisation_version || null;
  // Domaine : celui réellement utilisé par le moteur d'abord (analytics.tension_domain).
  // L'ancien repli sur la grille CMDPS était vide en BT ≤ 36 kVA (pas de ligne de dépassement).
  const tensionDomain = resolveTensionDomain({ analytics, enedisInfos });
  const cmdpsRule = getCmdpsRule(tensionDomain, analytics.tariff_grid?.cmdps_params);
  const segment = enedisInfos?.segment || null;
  const nbPoints = analytics.nb_points_analyses ?? null;

  const turpe6Total = parseFloat(turpe6.cout_total_annuel) || 0;
  const turpe7Total = parseFloat(turpe7.cout_total_annuel) || 0;
  const savings = turpe6Total - turpe7Total;
  const percentage = turpe6Total > 0 ? ((savings / turpe6Total) * 100).toFixed(1) : 0;

  const adresseStr = pdlData?.address
    ? `${pdlData.address.street}, ${pdlData.address.postal_code} ${pdlData.address.city}`
    : 'Non renseignée';

  return (
    <div className="p-2 space-y-2 max-h-[calc(100vh-180px)] overflow-y-auto">
      {/* Informations contractuelles — ligne unique dense */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-2 py-1.5 bg-gray-50 border border-gray-200 rounded text-[10px]">
        <span className="inline-flex items-center gap-1">
          <span className="text-gray-500 uppercase tracking-wide text-[9px]">PDL</span>
          <span className="font-mono font-semibold text-gray-900">{pdlData?.pdl_number || 'N/A'}</span>
        </span>
        <span className="text-gray-300">|</span>
        <span className="inline-flex items-center gap-1">
          <span className="text-gray-500 uppercase tracking-wide text-[9px]">Site</span>
          <span className="font-semibold text-gray-900">{pdlData?.site_name || 'N/A'}</span>
        </span>
        <span className="text-gray-300">|</span>
        <span className="inline-flex items-center gap-1 min-w-0">
          <span className="text-gray-500 uppercase tracking-wide text-[9px]">Adresse</span>
          <span className="text-gray-700 truncate">{adresseStr}</span>
        </span>
        <span className="text-gray-300">|</span>
        <span className="inline-flex items-center gap-1">
          <span className="text-gray-500 uppercase tracking-wide text-[9px]">Formule</span>
          <span className="font-semibold text-emerald-700">{optionTarifaire || '—'}{segment ? ` (${segment})` : ''}</span>
        </span>
        <span className="text-gray-300">|</span>
        <span className="inline-flex items-center gap-1">
          <span className="text-gray-500 uppercase tracking-wide text-[9px]">Domaine</span>
          <span className="font-semibold text-gray-900">{tensionDomain || '—'}</span>
        </span>
        <span className="text-gray-300">|</span>
        <span className="inline-flex items-center gap-1">
          <span className="text-gray-500 uppercase tracking-wide text-[9px]">Période</span>
          <span className="font-semibold text-gray-900">{year ? `01/01/${year} → 31/12/${year}` : '—'}</span>
        </span>
        <span className="text-gray-300">|</span>
        <span className="inline-flex items-center gap-1">
          <span className="text-gray-500 uppercase tracking-wide text-[9px]">Points</span>
          <span className="font-mono font-semibold text-gray-900">{nbPoints != null ? nbPoints.toLocaleString('fr-FR') : '—'}</span>
        </span>
      </div>

      {/* Comparatif TURPE 6 vs 7 — ligne synthétique + tableau composantes */}
      <section className="border border-gray-200 rounded">
        <div className="flex items-center justify-between px-2 py-1 bg-gray-50 border-b border-gray-200">
          <h4 className="text-[10px] font-semibold text-gray-600 uppercase tracking-wide">Comparatif TURPE 6 vs TURPE 7</h4>
          <div className="flex items-center gap-3 text-[10px]">
            <span><span className="text-gray-500">T6:</span> <span className="font-bold text-blue-600">{turpe6Total.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}</span></span>
            <span className="text-gray-300">•</span>
            <span><span className="text-gray-500">T7:</span> <span className="font-bold text-green-600">{turpe7Total.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}</span></span>
            <span className="text-gray-300">•</span>
            <span><span className="text-gray-500">{savings >= 0 ? 'Économie' : 'Surcoût'}:</span> <span className={`font-bold ${savings >= 0 ? 'text-green-600' : 'text-red-600'}`}>{Math.abs(savings).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })} ({savings >= 0 ? '-' : '+'}{Math.abs(percentage)}%)</span></span>
          </div>
        </div>

        {/* Détail des composantes (tableau ultra-compact) */}
        <div className="overflow-x-auto">
          <table className="w-full text-[10px]">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-1 px-2 font-medium text-gray-500 uppercase tracking-wide text-[9px]">Composante</th>
                <th className="text-right py-1 px-2 font-medium text-gray-500 uppercase tracking-wide text-[9px]">TURPE 6</th>
                <th className="text-right py-1 px-2 font-medium text-gray-500 uppercase tracking-wide text-[9px]">TURPE 7</th>
                <th className="text-right py-1 px-2 font-medium text-gray-500 uppercase tracking-wide text-[9px]">Écart</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {[
                { label: 'Gestion', t6: turpe6.composante_gestion || 0, t7: turpe7.composante_gestion || 0 },
                { label: 'Comptage', t6: turpe6.composante_comptage || 0, t7: turpe7.composante_comptage || 0 },
                { label: 'Soutirage Fixe', t6: turpe6.composante_soutirage_fixe || 0, t7: turpe7.composante_soutirage_fixe || 0 },
                { label: 'Soutirage Variable', t6: turpe6.composante_soutirage_variable || 0, t7: turpe7.composante_soutirage_variable || 0 },
                { label: 'Dépassements (CMDPS)', t6: turpe6.composante_depassement || 0, t7: turpe7.composante_depassement || 0 },
                { label: 'CACNC', t6: null, t7: turpe7.composante_cacnc || 0 },
              ].map((row, idx) => {
                const ecart = (row.t7 || 0) - (row.t6 || 0);
                return (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="py-0.5 px-2 text-gray-700">{row.label}</td>
                    <td className="text-right py-0.5 px-2 font-mono">{row.t6 == null ? '—' : `${row.t6.toLocaleString('fr-FR')} €`}</td>
                    <td className="text-right py-0.5 px-2 font-mono">{row.t7.toLocaleString('fr-FR')} €</td>
                    <td className={`text-right py-0.5 px-2 font-mono font-medium ${ecart > 0 ? 'text-red-600' : ecart < 0 ? 'text-green-600' : 'text-gray-500'}`}>
                      {ecart.toLocaleString('fr-FR')} €
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* Puissances souscrites + max atteintes — composantes tarifaires retirées (redondant avec table TURPE 6 vs 7 plus haut) */}
      <section className="border border-gray-200 rounded">
        <div className="flex items-center justify-between px-2 py-1 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center">
            <FiZap className="mr-1.5 h-3 w-3 text-gray-500" />
            <h4 className="text-[10px] font-semibold text-gray-600 uppercase tracking-wide">Puissances par cadran</h4>
          </div>
          <div className="flex items-center gap-2 text-[9px]">
            <span className="inline-flex items-center gap-0.5"><span className="w-1.5 h-1.5 bg-indigo-100 border border-indigo-300 rounded-sm"></span><span className="text-indigo-700">Souscrite</span></span>
            <span className="inline-flex items-center gap-0.5"><span className="w-1.5 h-1.5 bg-green-100 border border-green-300 rounded-sm"></span><span className="text-green-700">Max OK</span></span>
            {cmdpsRule.billed === false ? (
              <span className="inline-flex items-center gap-0.5"><span className="w-1.5 h-1.5 bg-amber-100 border border-amber-300 rounded-sm"></span><span className="text-amber-700">Risque coupure</span></span>
            ) : (
              <span className="inline-flex items-center gap-0.5"><span className="w-1.5 h-1.5 bg-red-100 border border-red-300 rounded-sm"></span><span className="text-red-700">Dépass.</span></span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 p-2">
          <div>
            <h5 className="text-[9px] font-semibold text-indigo-700 uppercase tracking-wide mb-1">Souscrites</h5>
            <div className="grid grid-cols-4 gap-1">
              {['HPH', 'HCH', 'HPB', 'HCB'].map((periode) => (
                <div key={periode} className="text-center px-1 py-0.5 bg-indigo-50 border border-indigo-200 rounded">
                  <div className="text-[9px] text-gray-600">{periode}</div>
                  <div className="text-[10px] font-bold text-indigo-700">{psByCadran[periode] != null ? `${psByCadran[periode]} kVA` : '—'}</div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h5 className="text-[9px] font-semibold text-orange-700 uppercase tracking-wide mb-1">Max atteintes</h5>
            <div className="grid grid-cols-4 gap-1">
                {(() => {
                  const analytics = turpe6.details_calcul?.analytics || {};
                  const depassements = turpe6.depassements_detectes || [];

                  let psMaxValues = {
                    'HPH': analytics.puissance_max_hph_kw || 0,
                    'HCH': analytics.puissance_max_hch_kw || 0,
                    'HPB': analytics.puissance_max_hpb_kw || 0,
                    'HCB': analytics.puissance_max_hcb_kw || 0,
                  };

                  if (Object.values(psMaxValues).every(v => v === 0) && depassements.length > 0) {
                    depassements.forEach(d => {
                      const pMax = parseFloat(d.puissance_max_kw || 0);
                      if (pMax > (psMaxValues[d.period] || 0)) {
                        psMaxValues[d.period] = pMax;
                      }
                    });
                  }

                  // ⚠️ Plus de fallback global × ratios fictifs (0.8/0.9/0.7),
                  // ni de fallback sur la PS souscrite : si la mesure d'une
                  // période est à 0, on affiche 0 — c'est l'info réelle
                  // (le PDL n'a pas eu de conso dans cette période, ex. site
                  // saisonnier sans hiver). Caroline 29/06 : PDL GAEC sans
                  // conso hiver affichait 180 kVA = PS, trompeur.

                  return ['HPH', 'HCH', 'HPB', 'HCB'].map((periode) => {
                    const psMax = parseFloat(psMaxValues[periode] || 0);
                    // PS du cadran lui-même (et non celle d'HPH) : en C4 les PS diffèrent par cadran.
                    const psSubscribed = parseFloat(psByCadran[periode] || 0);
                    const depassement = psMax > psSubscribed;
                    const displayPower = psMax;
                    // BT ≤ 36 kVA : aucun CMDPS, un max au-dessus de la PS = risque de coupure.
                    const tone = !depassement ? 'ok' : cmdpsRule.billed === false ? 'warn' : 'over';
                    const toneBox = { ok: 'bg-green-50 border-green-200', warn: 'bg-amber-50 border-amber-300', over: 'bg-red-100 border-red-300' }[tone];
                    const toneText = { ok: 'text-green-700', warn: 'text-amber-700', over: 'text-red-700' }[tone];

                    // Affichage en kVA pour homogénéité avec la PS souscrite
                    // (contrats ENEDIS en kVA). La R63 mesure la puissance active
                    // en kW ; sur les sites C4 majoritaires (cos φ ≈ 1 grâce à la
                    // compensation réactive), S ≈ P donc l'approximation est valide.
                    // Si on veut le calcul exact S = √(P² + Q²), il faudrait
                    // remonter la puissance réactive (puissance_reactive_kvar) du
                    // payload turpe_engine — TODO si besoin de précision.
                    return (
                      <div key={periode} className={`text-center px-1 py-0.5 rounded border ${toneBox}`}
                        title={tone === 'warn'
                          ? 'Max au-dessus de la PS : non facturé en BT ≤ 36 kVA, mais risque de coupure par le compteur'
                          : 'Puissance active maximale relevée par la R63 (≈ kVA avec cos φ ≈ 1)'}>
                        <div className="text-[9px] text-gray-600">{periode}</div>
                        <div className={`text-[10px] font-bold ${toneText}`}>
                          {displayPower.toFixed(1)} kVA
                        </div>
                        {depassement && (
                          <div className={`text-[9px] leading-none ${toneText}`}>
                            +{(psMax - psSubscribed).toFixed(1)}
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
              {cmdpsRule.billed === false && (
                <p className="mt-1 text-[9px] text-amber-700 leading-tight">
                  Dépassement non facturé en BT ≤ 36 kVA : le compteur coupe au-delà de la PS (risque de coupure).
                </p>
              )}
            </div>
          </div>
        </section>

      {/* Détail du calcul CMDPS — compact */}
      <section className="border border-gray-200 rounded">
        <div className="flex items-center px-2 py-1 bg-gray-50 border-b border-gray-200">
          <FiZap className="mr-1.5 h-3 w-3 text-gray-500" />
          <h4 className="text-[10px] font-semibold text-gray-600 uppercase tracking-wide">Détail du calcul CMDPS</h4>
        </div>

        {(() => {
          // Le moteur sérialise les Decimal en chaîne ("0.00") : conversion avant formatage.
          const cmdpsTotal = Number(turpe6.composante_depassement) || 0;

          return (
            <div className="p-2 space-y-2">
              {/* Formule du domaine (miroir du moteur) + total en ligne */}
              <div className="flex items-center justify-between gap-2 px-2 py-1 bg-purple-50 border border-purple-200 rounded">
                <div className="flex items-center gap-2 text-[10px] flex-wrap">
                  <span className="font-mono bg-white border border-purple-200 px-1.5 py-0.5 rounded text-purple-800 font-semibold">{cmdpsRule.formula}</span>
                  {cmdpsRule.parameter && (
                    <span className="font-mono font-bold text-purple-700">{cmdpsRule.parameter}</span>
                  )}
                  <span className="text-gray-400">|</span>
                  <span className="text-gray-500">Domaine =</span> <span className="text-purple-700">{tensionDomain || '—'}</span>
                </div>
                <span className="text-[10px] font-bold text-purple-800 whitespace-nowrap">
                  Total : {cmdpsTotal.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
                </span>
              </div>

              {/* Explication + optimisations — 2 colonnes */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                <div className="px-2 py-1 bg-blue-50 border border-blue-200 rounded">
                  <h6 className="text-[9px] font-semibold text-blue-700 uppercase tracking-wide mb-0.5 flex items-center">
                    <FiInfo className="mr-1 h-2.5 w-2.5" />
                    Explication
                  </h6>
                  <ul className="text-[10px] text-blue-800 leading-tight space-y-0.5">
                    {cmdpsRule.explanation.map((line) => (
                      <li key={line}>• {line}</li>
                    ))}
                  </ul>
                </div>

                <div className="px-2 py-1 bg-yellow-50 border border-yellow-200 rounded">
                  <h6 className="text-[9px] font-semibold text-yellow-700 uppercase tracking-wide mb-0.5 flex items-center">
                    <FiTarget className="mr-1 h-2.5 w-2.5" />
                    Optimisations CMDPS
                  </h6>
                  <div className="grid grid-cols-2 gap-2 text-[10px] text-yellow-800 leading-tight">
                    <ul className="space-y-0.5">
                      <li className="font-semibold text-yellow-700">Techniques :</li>
                      <li>• Délesteur auto</li>
                      <li>• Gestion charges</li>
                      <li>• Stockage batterie</li>
                      <li>• Étalement conso</li>
                    </ul>
                    <ul className="space-y-0.5">
                      <li className="font-semibold text-yellow-700">Contractuelles :</li>
                      <li>• ↑ P souscrite</li>
                      <li>• Chgt option tarifaire</li>
                      <li>• Négo GRD</li>
                      <li>• Optim. périodes</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </section>
    </div>
  );
};

// ===== ONGLET COURBE DE CHARGE =====
const CourbeChargeTab = ({ results, pdlId, year, session }) => {
  const turpe6 = results?.turpe6_details || {};
  const subscribedPower = turpe6.composantes_detail?.CS_FIXE?.parametres?.ps_hph_kw ?? 0;

  // Default : année d'analyse complète, granularité 1h (~8760 pts → reasonnable)
  const defaultStart = year ? `${year}-01-01` : new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().slice(0, 10);
  const defaultEnd = year ? `${year}-12-31` : new Date().toISOString().slice(0, 10);
  const [granularity, setGranularity] = useState('1h');
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState({ pas_natif_minutes: null, count: 0 });

  useEffect(() => {
    if (!pdlId || !session?.access_token || !startDate || !endDate) return;
    let cancelled = false;
    setLoading(true);
    const url = `/api/enedis/courbe-charge?pdl_id=${pdlId}&start=${startDate}T00:00:00Z&end=${endDate}T23:59:59Z&granularity=${granularity}`;
    fetch(url, { headers: { Authorization: `Bearer ${session.access_token}` } })
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;
        if (data.success) {
          setPoints((data.points || []).map(p => ({ timestamp: p.timestamp, power: p.power_kw })));
          setMeta({ pas_natif_minutes: data.pas_natif_minutes, count: data.count });
        } else {
          setPoints([]);
        }
      })
      .catch(() => !cancelled && setPoints([]))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [pdlId, session?.access_token, startDate, endDate, granularity]);

  const powers = points.map(d => d.power).filter(p => p != null && p > 0);
  const maxDataPower = powers.length > 0 ? Math.max(...powers) : 60;
  const yAxisMax = Math.max(subscribedPower * 1.2, maxDataPower * 1.1);

  return (
    <div className="border border-gray-200 rounded m-1">
      <div className="flex items-center justify-between gap-2 px-2 py-1 bg-gray-50 border-b border-gray-200 flex-wrap">
        <h4 className="font-semibold text-gray-600 flex items-center text-[10px] uppercase tracking-wide">
          <FiActivity className="mr-1 h-3 w-3 text-blue-600" />
          Courbe de charge
          {meta.count > 0 && <span className="ml-2 text-[9px] font-normal text-gray-500">({meta.count} pts • pas natif {meta.pas_natif_minutes} min)</span>}
        </h4>
        <div className="flex items-center gap-2 text-[10px]">
          <label className="flex items-center gap-1">
            <span className="text-gray-500">Du</span>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="border border-gray-300 rounded px-1 py-0.5 text-[10px]" />
          </label>
          <label className="flex items-center gap-1">
            <span className="text-gray-500">Au</span>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="border border-gray-300 rounded px-1 py-0.5 text-[10px]" />
          </label>
          <label className="flex items-center gap-1">
            <span className="text-gray-500">Pas</span>
            <select value={granularity} onChange={e => setGranularity(e.target.value)} className="border border-gray-300 rounded px-1 py-0.5 text-[10px]">
              <option value="5m">5 min</option>
              <option value="30m">30 min</option>
              <option value="1h">1 h</option>
              <option value="1d">1 j</option>
            </select>
          </label>
          <span className="inline-flex items-center gap-1 ml-1"><span className="w-2 h-0.5 bg-blue-500"></span><span className="text-gray-600">Mesurée</span></span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-0.5 border-t border-dashed border-red-500"></span><span className="text-gray-600">PS {subscribedPower} kVA</span></span>
        </div>
      </div>

      <div style={{ height: '320px' }}>
        {loading ? (
          <div className="h-full flex items-center justify-center text-[10px] text-gray-500"><FiLoader className="animate-spin mr-2" /> Chargement…</div>
        ) : points.length === 0 ? (
          <div className="h-full flex items-center justify-center text-[10px] text-gray-500">Aucune donnée pour cette période</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={points} margin={{ top: 6, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="timestamp"
                tickFormatter={(value) => {
                  const d = new Date(value);
                  return granularity === '1d'
                    ? d.toLocaleDateString('fr-FR', { month: 'short', day: 'numeric' })
                    : d.toLocaleString('fr-FR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
                }}
                tick={{ fontSize: 9 }}
                stroke="#6b7280"
                minTickGap={40}
              />
              <YAxis
                label={{ value: 'kW', angle: -90, position: 'insideLeft', offset: 12, style: { fontSize: 9, fill: '#6b7280' } }}
                tick={{ fontSize: 9 }}
                stroke="#6b7280"
                domain={[0, yAxisMax]}
                width={32}
              />
              <Tooltip
                labelFormatter={(value) => new Date(value).toLocaleString('fr-FR')}
                formatter={(value, name) => name === 'Puissance mesurée' ? [`${value} kW`, 'P mesurée'] : [`${value} kVA`, 'PS']}
                contentStyle={{ fontSize: '9px', padding: '4px 6px', border: '1px solid #e5e7eb', borderRadius: '4px' }}
              />
              <Line type="monotone" dataKey="power" stroke="#3B82F6" strokeWidth={1.2} dot={false} name="Puissance mesurée" />
              <ReferenceLine
                y={subscribedPower}
                stroke="#EF4444"
                strokeWidth={1.5}
                strokeDasharray="6 3"
                label={{ value: `PS ${subscribedPower} kVA`, position: 'insideTopRight', offset: 4, style: { fill: '#EF4444', fontWeight: 'bold', fontSize: '9px' } }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

// ===== ONGLET DÉPASSEMENTS =====
const DepassementsTab = ({ results, pdlData }) => {
  const turpe6 = results?.turpe6_details || {};
  const depassements = turpe6.depassements_detectes || [];
  // Le moteur sérialise les Decimal en chaîne ("0.00") : conversion avant formatage.
  const cmdpsTotal = Number(turpe6.composante_depassement) || 0;
  const analytics = turpe6.details_calcul?.analytics || {};
  const enedisInfos = Array.isArray(pdlData?.enedis_infos) ? pdlData?.enedis_infos[0] : pdlData?.enedis_infos;
  const cmdpsRule = getCmdpsRule(resolveTensionDomain({ analytics, enedisInfos }), analytics.tariff_grid?.cmdps_params);
  const subscribedPower = turpe6.composantes_detail?.CS_FIXE?.parametres?.ps_hph_kw ?? 0;

  // BT ≤ 36 kVA : liste toujours vide faute de CMDPS, sans que la PS soit forcément respectée.
  if (depassements.length === 0 && cmdpsRule.billed === false) {
    return (
      <div className="p-3 flex items-center justify-center h-40">
        <div className="text-center bg-amber-50 rounded border border-amber-200 px-4 py-3 max-w-md">
          <FiInfo className="h-5 w-5 text-amber-600 mx-auto mb-1" />
          <p className="text-amber-800 font-semibold text-[11px]">Aucun dépassement facturé en BT ≤ 36 kVA</p>
          <p className="text-amber-700 text-[10px]">Le TURPE ne prévoit pas de CMDPS sur ce domaine. Une puissance mesurée au-dessus de la PS expose en revanche à une coupure par le compteur : voir les max atteintes dans la Synthèse.</p>
        </div>
      </div>
    );
  }

  if (depassements.length === 0) {
    return (
      <div className="p-3 flex items-center justify-center h-40">
        <div className="text-center bg-green-50 rounded border border-green-200 px-4 py-3">
          <FiCheckCircle className="h-5 w-5 text-green-600 mx-auto mb-1" />
          <p className="text-green-800 font-semibold text-[11px]">Aucun dépassement détecté</p>
          <p className="text-green-600 text-[10px]">Toutes vos consommations respectent les puissances souscrites.</p>
        </div>
      </div>
    );
  }

  const dureeTotale = depassements.reduce((sum, d) => sum + (d.duree_minutes || 10), 0) / 60;

  return (
    <div className="p-2 space-y-2 max-h-[calc(100vh-180px)] overflow-y-auto">
      {/* Ligne résumé + formule unique */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-2 py-1.5 bg-gray-50 border border-gray-200 rounded text-[10px]">
        <span className="inline-flex items-center gap-1">
          <span className="text-blue-600 uppercase tracking-wide text-[9px]">Durée totale</span>
          <span className="font-bold text-blue-800">{dureeTotale.toFixed(1)} h</span>
          <span className="text-blue-600">({depassements.length} évts)</span>
        </span>
        <span className="text-gray-300">|</span>
        <span className="inline-flex items-center gap-1">
          <span className="text-red-600 uppercase tracking-wide text-[9px]">Coût annuel</span>
          <span className="font-bold text-red-800">{cmdpsTotal.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}</span>
        </span>
        <span className="text-gray-300">|</span>
        <span className="font-mono bg-white border border-purple-200 px-1.5 py-0.5 rounded text-purple-800 font-semibold">{cmdpsRule.formula}</span>
        {cmdpsRule.parameter && (
          <span className="font-mono font-bold text-purple-700">{cmdpsRule.parameter}</span>
        )}
      </div>

      {/* Tableau détaillé ultra-compact */}
      <section className="border border-gray-200 rounded">
        <div className="flex items-center px-2 py-1 bg-gray-50 border-b border-gray-200">
          <h5 className="text-[10px] font-semibold text-gray-600 uppercase tracking-wide">Calcul détaillé par mois</h5>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[10px]">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-1 px-2 font-medium text-gray-500 uppercase tracking-wide text-[9px]">Mois</th>
                <th className="text-center py-1 px-2 font-medium text-gray-500 uppercase tracking-wide text-[9px]">Période</th>
                <th className="text-right py-1 px-2 font-medium text-gray-500 uppercase tracking-wide text-[9px]">PS (kW)</th>
                <th className="text-right py-1 px-2 font-medium text-gray-500 uppercase tracking-wide text-[9px]">P_max (kW)</th>
                <th className="text-right py-1 px-2 font-medium text-gray-500 uppercase tracking-wide text-[9px]">Dépass.</th>
                <th className="text-right py-1 px-2 font-medium text-gray-500 uppercase tracking-wide text-[9px]">Coût (€)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {depassements.map((depass, index) => {
                const date = new Date(depass.timestamp);
                const mois = date.toLocaleDateString('fr-FR', {
                  year: 'numeric',
                  month: 'short'
                });
                const depassement_kw = parseFloat(depass.depassement_kw || 0);
                const ps_kw = parseFloat(depass.puissance_souscrite_kw || subscribedPower);
                const p_max_kw = parseFloat(depass.puissance_max_kw || 0);
                const cout = parseFloat(depass.cout_cmdps_eur || 0);

                return (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="py-0.5 px-2 font-medium">{mois}</td>
                    <td className="text-center py-0.5 px-2">
                      <span className="bg-blue-100 text-blue-800 px-1.5 py-0 rounded text-[9px]">
                        {depass.period}
                      </span>
                    </td>
                    <td className="text-right py-0.5 px-2 font-mono">{ps_kw.toFixed(1)}</td>
                    <td className="text-right py-0.5 px-2 font-mono font-bold text-red-600">
                      {p_max_kw.toFixed(1)}
                    </td>
                    <td className="text-right py-0.5 px-2 font-mono font-bold text-orange-600">
                      +{depassement_kw.toFixed(1)}
                    </td>
                    <td className="text-right py-0.5 px-2 font-bold text-purple-700">
                      {cout.toLocaleString('fr-FR', {
                        style: 'currency',
                        currency: 'EUR',
                        minimumFractionDigits: 2
                      })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-purple-100 border-t-2 border-purple-300">
                <td colSpan="5" className="py-0.5 px-2 text-right font-bold text-purple-800 text-[10px]">
                  TOTAL CMDPS ANNUEL :
                </td>
                <td className="py-0.5 px-2 text-right text-[10px] font-bold text-purple-800">
                  {cmdpsTotal.toLocaleString('fr-FR', {
                    style: 'currency',
                    currency: 'EUR'
                  })}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>

      {/* Répartition mensuelle */}
      <section className="border border-gray-200 rounded">
        <div className="flex items-center px-2 py-1 bg-gray-50 border-b border-gray-200">
          <h5 className="text-[10px] font-semibold text-gray-600 uppercase tracking-wide">Répartition mensuelle des dépassements</h5>
        </div>
        <div className="p-2">
          <DepassementsParMois depassements={depassements} />
        </div>
      </section>
    </div>
  );
};

// ===== ONGLET OPTIMISATION CONTRACTUELLE =====
const OptimisationTab = ({ results, pdlData, session, analysisYear, onAnalysisUpdated, blocage = null, onRelancer = null }) => {
  const pdlId = pdlData?.id;

  // États pour gérer l'API
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analysisSummary, setAnalysisSummary] = useState({
    best_saving_eur: 0,
    version_of_use_saving_eur: 0,
    manual_saving_eur: 0,
    best_saving_strategy_json: null,
    version_of_use_details_json: null,
    manual_strategy_json: null,
    chosen_optimization: null,
  });
  const [selectedOptimization, setSelectedOptimization] = useState(null);
  const [showFinalChoiceModal, setShowFinalChoiceModal] = useState(false);

  // ✅ DÉPLACER optimizationCards À L'INTÉRIEUR du composant
  const optimizationCards = [
    {
      id: 'auto',
      title: 'Optimisation Automatique',
      description: 'IA qui analyse vos données et propose des stratégies personnalisées',
      icon: '🤖',
      color: 'blue',
      buttonText: 'Lancer l\'analyse IA'
    },
    {
      id: 'manual',
      title: 'Optimisation Manuelle',
      description: 'Ajustez les puissances avec des sliders et visualisez l\'impact temps réel',
      icon: '⚡',
      color: 'orange',
      buttonText: 'Simulateur manuel'
    },
    {
      id: 'version',
      title: 'Version d\'Utilisation',
      description: 'Analyse comparative CU / MU / LU pour identifier des économies cachées',
      icon: '📊',
      color: 'purple',
      buttonText: 'Analyser CU / MU / LU'
    }
  ];

  useEffect(() => {
    const fetchSavedSummary = async () => {
      if (!pdlId || !session?.access_token) {
        setIsLoading(false);
        return; 
      }

      setIsLoading(true);
      setError(null);
      
      try {
        const response = await fetch(`/api/turpe/get-analysis-summary/${pdlId}`, {
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        });

        if (!response.ok) {
          throw new Error('Erreur réseau lors de la récupération du résumé.');
        }

        const responseData = await response.json();
        
        if (responseData.success && responseData.data) {
          setAnalysisSummary(responseData.data);
          // Auto-ouvrir le panel "auto" si une optimisation est déjà persistée,
          // pour que le user voie ses résultats au retour sur la page.
          if (responseData.data.best_saving_strategy_json && !selectedOptimization) {
            setSelectedOptimization('auto');
          } else if (responseData.data.version_of_use_details_json && !selectedOptimization) {
            setSelectedOptimization('version');
          }
        }

      } catch (err) {
        console.error("Erreur de chargement du résumé d'analyse:", err);
        setError("Impossible de charger les optimisations sauvegardées.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchSavedSummary();
  }, [pdlId, session]);

  const handleOptimizationComplete = async (type, rawResult) => {
    setSelectedOptimization(type);

    let analysis_type;
    if (type === 'auto') analysis_type = 'auto_optimization';
    if (type === 'manual') analysis_type = 'manual_optimization';
    if (type === 'version') analysis_type = 'version_of_use';
    if (!analysis_type) return;

    // Helper : retourne la stratégie retenue. Priorité au choix utilisateur
    // (is_recommended posé par "Choisir cette stratégie" dans le panel) ;
    // fallback sort desc si aucune is_recommended (cas legacy).
    const pickStrategy = (strategies) => {
      const viable = (strategies || []).filter(s => s.annual_saving_eur > 0);
      return viable.find(s => s.is_recommended) || viable.sort((a, b) => b.annual_saving_eur - a.annual_saving_eur)[0];
    };

    const newSummary = { ...analysisSummary };
    if (analysis_type === 'auto_optimization') {
        const bestStrategy = pickStrategy(rawResult.strategies);
        if (bestStrategy) {
            newSummary.best_saving_eur = bestStrategy.annual_saving_eur;
            newSummary.best_saving_strategy_json = bestStrategy;
        }
    }
    if (analysis_type === 'manual_optimization') {
        const savings = Number(rawResult?.impact_analysis?.annual_savings) || 0;
        newSummary.manual_saving_eur = savings;
        newSummary.manual_strategy_json = rawResult;
    }
    if (analysis_type === 'version_of_use') {
        newSummary.version_of_use_saving_eur = rawResult.economie_annuelle;
        newSummary.recommended_version_of_use = rawResult.recommended_version;
        newSummary.version_of_use_costs = rawResult.couts_par_version || null;
        newSummary.current_version_of_use = rawResult.current_version || null;
    }
    setAnalysisSummary(newSummary);

    let payload_data;
    if (analysis_type === 'auto_optimization') {
      const bestStrategy = pickStrategy(rawResult.strategies);
      payload_data = { strategies: bestStrategy ? [bestStrategy] : [] };
    } else {
      payload_data = rawResult;
    }

    try {
      const response = await fetch('/api/turpe/update-analysis-result', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          pdl_id: pdlId,
          analysis_type: analysis_type,
          result_data: payload_data,
          analysis_year: analysisYear
        })
      });

      if (!response.ok) {
        throw new Error(`La sauvegarde a échoué avec le statut: ${response.status}`);
      }

      const savedData = await response.json();
      toast.success("Résultats sauvegardés !");
      // Le résumé partagé (statut, économies) est calculé par le serveur : on le relit.
      try {
        const r = await fetch(`/api/turpe/get-analysis-summary/${pdlId}`, { headers: { Authorization: `Bearer ${session.access_token}` } });
        const relu = await lireReponseApi(r, 'Relecture du résumé');
        if (relu?.data) { Object.assign(newSummary, relu.data); setAnalysisSummary({ ...newSummary }); }
      } catch (e) {
        console.error('[AnalyseTurpe] relecture du résumé', e);
        toast.error(e.message);
      }

      if (onAnalysisUpdated && savedData.success && savedData.data) {
        // On propage le summary COMPLET au parent (pas juste 2 champs) pour que
        // CompactKPICards puisse calculer l'éco totale cohérente avec le choix
        // retenu (chosen_optimization + chosen_version_of_use), pas seulement
        // economie_potentielle_max (= snapshot analyse auto).
        onAnalysisUpdated({ ...newSummary, ...savedData.data });
      }
      
    } catch (err) {
      console.error("Erreur de sauvegarde de l'optimisation :", err);
      toast.error("La sauvegarde des résultats a échoué.");
    }
  };

  const getOptimizationStatus = (type) => {
    let isCompleted = false;
    if (analysisSummary) {
        // "Analysé" dès qu'une analyse a été enregistrée, même si l'éco est 0
        // (cas où la stratégie testée n'apporte rien). On regarde l'existence
        // du strategy_json (= rawResult persisté), pas le montant du saving.
        if (type === 'auto')    isCompleted = !!analysisSummary.best_saving_strategy_json
          || (!!analysisSummary.best_saving_eur && analysisSummary.best_saving_eur > 0);
        if (type === 'manual')  isCompleted = !!analysisSummary.manual_strategy_json
          || (!!analysisSummary.manual_saving_eur && analysisSummary.manual_saving_eur > 0);
        if (type === 'version') isCompleted = !!analysisSummary.recommended_version_of_use
          || !!analysisSummary.version_of_use_details_json;
    }
    if (isCompleted) return { status: 'completed', text: 'Analysé', color: 'green' };
    return { status: 'idle', text: 'À analyser', color: 'gray' };
  };

  const getOptimizationSavings = (type) => {
    if (!analysisSummary) return 0;
    if (type === 'auto')    return analysisSummary.best_saving_eur || 0;
    if (type === 'manual')  return analysisSummary.manual_saving_eur || 0;
    // Résumé partagé : null = non mesurée / surcoût / analyse non concluante (jamais 0 inventé).
    if (type === 'version') return economieVersionDuResume(analysisSummary);
    return 0;
  };

  // Délègue au helper centralisé computeTotalSavings (utilisé aussi par
  // CompactKPICards, pour rester cohérent entre KPI top et bandeau).
  const getTotalSavings = () => computeTotalSavings(analysisSummary, 0);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getCompletedCount = () => {
    if (!analysisSummary) return 0;
    // Cohérent avec getOptimizationStatus : on compte "Analysé" dès qu'une
    // analyse a été persistée (strategy_json présent), même si l'éco = 0 €.
    const autoDone    = !!analysisSummary.best_saving_strategy_json
      || (!!analysisSummary.best_saving_eur && analysisSummary.best_saving_eur > 0);
    const manualDone  = !!analysisSummary.manual_strategy_json
      || (!!analysisSummary.manual_saving_eur && analysisSummary.manual_saving_eur > 0);
    const versionDone = !!analysisSummary.recommended_version_of_use
      || !!analysisSummary.version_of_use_details_json;
    return [autoDone, manualDone, versionDone].filter(Boolean).length;
  };

  const isAnalysisCompleted = (type) => {
    if (!analysisSummary) return false;
    if (type === 'auto') {
      return !!analysisSummary.best_saving_strategy_json
        || (!!analysisSummary.best_saving_eur && analysisSummary.best_saving_eur > 0);
    }
    if (type === 'manual') {
      return !!analysisSummary.manual_strategy_json
        || (!!analysisSummary.manual_saving_eur && analysisSummary.manual_saving_eur > 0);
    }
    if (type === 'version') {
      return !!analysisSummary.recommended_version_of_use || !!analysisSummary.version_of_use_details_json;
    }
    return false;
  };

  const renderOptimizationDetails = () => {
    if (!selectedOptimization) return null;

    switch (selectedOptimization) {
      case 'auto':
        return (
          <div className="mt-6 bg-white border border-gray-200 rounded-lg shadow-sm">
            <SmartOptimizationPanel
              results={results}
              pdlData={pdlData}
              session={session}
              analysisYear={analysisYear}
              savedStrategy={analysisSummary?.best_saving_strategy_json}
              onOptimize={(result) => {
                handleOptimizationComplete('auto', result);
                toast.success('Optimisation automatique terminée !');
              }}
            />
          </div>
        );

      case 'manual':
        return (
          <div className="mt-6 bg-white border border-gray-200 rounded-lg shadow-sm">
            <ManualOptimizationPanel
              results={results}
              pdlData={pdlData}
              session={session}
              savedStrategy={analysisSummary?.manual_strategy_json}
              currentPowers={(() => {
                // Vraies PS dans composantes_detail.CS_FIXE.parametres (cf. fix SmartOptimizationPanel).
                const p = results?.turpe6_details?.composantes_detail?.CS_FIXE?.parametres;
                if (p?.ps_hph_kw != null) {
                  return { hph: p.ps_hph_kw, hch: p.ps_hch_kw, hpb: p.ps_hpb_kw, hcb: p.ps_hcb_kw };
                }
                return results?.turpe6_details?.power_subscriptions || { hph: 36, hch: 36, hpb: 36, hcb: 36 };
              })()}
              onOptimizationUpdate={(data) => {
                handleOptimizationComplete('manual', data);
              }}
            />
          </div>
        );

      case 'version':
        return (
          <div className="mt-6 bg-white border border-gray-200 rounded-lg shadow-sm">
            <VersionUtilisationOptimizer
              pdlData={pdlData}
              session={session}
              pdlId={pdlId}
              analysisYear={analysisYear}
              chosenVersionOfUse={analysisSummary?.chosen_version_of_use}
              savedAnalysis={analysisSummary?.version_of_use_details_json}
              onAnalysisComplete={(result) => {
                handleOptimizationComplete('version', result);
                toast.success('Analyse version d\'utilisation terminée !');
              }}
              onChosenVersionChange={(version) => {
                setAnalysisSummary(prev => ({ ...prev, chosen_version_of_use: version }));
                // Propager au parent → CompactKPICards recalcule le KPI éco totale
                if (onAnalysisUpdated) onAnalysisUpdated({ chosen_version_of_use: version });
              }}
            />
          </div>
        );

      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="p-3 text-center">
        <FiLoader className="animate-spin h-4 w-4 text-blue-600 mx-auto" />
        <p className="mt-1 text-[11px] text-gray-600">Chargement des optimisations...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-3 text-center text-red-600 bg-red-50 rounded">
        <FiAlertTriangle className="h-4 w-4 mx-auto mb-1" />
        <p className="font-semibold text-[11px]">Une erreur est survenue</p>
        <p className="text-[10px]">{error}</p>
      </div>
    );
  }

  // Analyse non concluante / non vérifiée (24/10/2026) : ni montant ni recommandation ; le
  // choix final est désactivé (motif au survol), les panneaux de résultats ne sont pas montrés.
  const motifPage = blocage || motifBlocageDe(analysisSummary);
  if (motifPage) {
    return (
      <div className="max-h-[calc(100vh-180px)] overflow-y-auto">
        <div className="p-2 space-y-2" data-testid="turpe-optimisation-bloquee">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-2 py-1.5 bg-gray-50 border border-gray-200 rounded text-[10px]">
            <h3 className="text-[10px] font-semibold text-gray-700 flex items-center uppercase tracking-wide">
              <FiTarget className="mr-1 h-3 w-3 text-gray-500" />
              Optimisations TURPE
            </h3>
            <span className="ml-auto" title={motifPage}>
              <button type="button" disabled data-testid="turpe-choix-final" title={motifPage}
                className="px-2 py-0.5 rounded text-[10px] font-semibold flex items-center gap-1 bg-emerald-600 text-white opacity-40 cursor-not-allowed pointer-events-none">
                <FiCheckCircle className="h-3 w-3" />
                Choix final
              </button>
            </span>
          </div>
          <div className="px-3 py-3 border border-amber-200 bg-amber-50 rounded text-[11px] text-amber-900">
            <p className="font-semibold">Aucun montant ni recommandation de puissance ou de version tant que l'analyse n'est pas concluante.</p>
            <p className="mt-0.5">{motifPage}</p>
            {onRelancer && <p className="mt-1">Utiliser « Relancer l'analyse » dans le bandeau ci-dessus : le calcul est refait en entier.</p>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-h-[calc(100vh-180px)] overflow-y-auto">
      <div className="p-2 space-y-2">

        {/* Header — ligne dense */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-2 py-1.5 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded text-[10px]">
          <h3 className="text-[10px] font-semibold text-gray-700 flex items-center uppercase tracking-wide">
            <FiTarget className="mr-1 h-3 w-3 text-blue-600" />
            Optimisations TURPE
          </h3>
          <span className="text-gray-300">|</span>
          <span><span className="text-gray-500 uppercase tracking-wide text-[9px]">{(analysisSummary?.chosen_optimization || analysisSummary?.chosen_version_of_use) ? 'Éco du choix retenu' : 'Meilleure éco'}</span> <span className="font-bold text-emerald-600">{formatCurrency(getTotalSavings())}/an</span></span>
          <span className="text-gray-300">|</span>
          <span><span className="text-gray-500 uppercase tracking-wide text-[9px]">Analyses</span> <span className="font-bold text-blue-600">{getCompletedCount()}/3</span></span>
          {analysisSummary?.recommended_version_of_use && (
            <>
              <span className="text-gray-300">|</span>
              <span><span className="text-gray-500 uppercase tracking-wide text-[9px]">Version recommandée</span> <span className="font-bold text-purple-600">{analysisSummary.recommended_version_of_use}</span></span>
            </>
          )}
          <span className="ml-auto">
            <button
              onClick={() => setShowFinalChoiceModal(true)}
              disabled={getCompletedCount() < 3}
              className="px-2 py-0.5 rounded text-[10px] font-semibold flex items-center gap-1 transition-colors bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
              title={getCompletedCount() < 3 ? 'Termine les 3 analyses pour faire ton choix final' : 'Choix final entre IA/Manuel et CU/LU'}
            >
              <FiCheckCircle className="h-3 w-3" />
              Choix final
            </button>
          </span>
        </div>

        {/* Récap du choix final si déjà validé */}
        {(analysisSummary?.chosen_optimization || analysisSummary?.chosen_version_of_use)
          && (!analysisSummary?.resume || estConcluante(analysisSummary)) && (() => {
          // Résumé PARTAGÉ : une économie de puissance négative n'est jamais présentée (null → 0 ici, masquée).
          const psEco = analysisSummary?.resume
            ? (Number(analysisSummary.resume.ps?.economie_eur) || 0)
            : (analysisSummary?.chosen_optimization ? getOptimizationSavings(analysisSummary.chosen_optimization) : 0);
          // Économie de version : résumé PARTAGÉ (coût actuelle − coût retenue ; null si non mesurée).
          const vouEco = Number(economieVersionDuResume(analysisSummary)) || 0;
          const totalEco = analysisSummary?.resume ? Number(analysisSummary.resume.economie_totale_eur) || 0 : (Number(psEco) || 0) + vouEco;
          return (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-2 py-1 bg-emerald-50 border border-emerald-200 rounded text-[10px]">
            <FiCheckCircle className="h-3 w-3 text-emerald-600" />
            <span className="font-semibold text-emerald-800">Choix final retenu :</span>
            {analysisSummary?.chosen_optimization && (
              <span>
                <span className="text-gray-500">Stratégie PS</span>{' '}
                <span className="font-bold text-emerald-700">
                  {analysisSummary.chosen_optimization === 'auto' ? 'Optimisation IA' : 'Optimisation manuelle'}
                </span>
                {' '}({formatCurrency(psEco)}/an)
              </span>
            )}
            {analysisSummary?.chosen_version_of_use && (
              <>
                <span className="text-gray-300">|</span>
                <span>
                  <span className="text-gray-500">Version d'utilisation</span>{' '}
                  <span className="font-bold text-emerald-700">{analysisSummary.chosen_version_of_use}</span>
                  {vouEco > 0 && <span className="text-emerald-600"> (+{formatCurrency(vouEco)}/an)</span>}
                </span>
              </>
            )}
            {totalEco > (Number(psEco) || 0) && (
              <>
                <span className="text-gray-300">|</span>
                <span>
                  <span className="text-gray-500">Éco totale</span>{' '}
                  <span className="font-bold text-emerald-700">{formatCurrency(totalEco)}/an</span>
                </span>
              </>
            )}
            <button
              onClick={() => setShowFinalChoiceModal(true)}
              className="ml-auto text-[9px] text-emerald-700 hover:text-emerald-900 underline"
            >
              Modifier
            </button>
          </div>
          );
        })()}

        {/* Cartes des optimisations */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
          {optimizationCards.map((card) => {
            const status = getOptimizationStatus(card.id);
            const savings = getOptimizationSavings(card.id);
            const isSelected = selectedOptimization === card.id;

            return (
              <div
                key={card.id}
                className={`bg-white border rounded p-2 transition-all duration-200 cursor-pointer hover:shadow-md ${
                  isSelected
                    ? `border-${card.color}-300 shadow-md bg-${card.color}-50`
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setSelectedOptimization(isSelected ? null : card.id)}
              >
                <div className="flex items-start justify-between mb-1.5">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm">{card.icon}</span>
                    <div className="min-w-0">
                      <h4 className="font-semibold text-gray-800 text-[11px] leading-tight">{card.title}</h4>
                      <span
                        className={`inline-block mt-0.5 px-1.5 py-0 rounded-full text-[9px] font-medium bg-${status.color}-100 text-${status.color}-800`}
                      >
                        {status.text}
                      </span>
                    </div>
                  </div>
                  <div className={`text-[9px] px-1 py-0 rounded ${isSelected ? 'bg-white text-gray-700' : 'bg-gray-100 text-gray-600'}`}>
                    {isSelected ? '▼' : '▶'}
                  </div>
                </div>

                <p className="text-[10px] text-gray-600 mb-1.5 leading-snug line-clamp-2">
                  {card.description}
                </p>

                {savings > 0 && (
                  <div className="mb-1.5 px-1.5 py-0.5 bg-emerald-50 rounded border border-emerald-200 flex items-center justify-between">
                    <span className="text-[9px] text-emerald-600 uppercase tracking-wide">Éco calculée</span>
                    <span className="text-[10px] font-bold text-emerald-700">{formatCurrency(savings)}/an</span>
                  </div>
                )}

                <button
                  className={`w-full text-[10px] px-2 py-1 rounded transition-colors ${
                    card.color === 'blue' ? 'bg-blue-600 hover:bg-blue-700 text-white' :
                    card.color === 'orange' ? 'bg-orange-600 hover:bg-orange-700 text-white' :
                    'bg-purple-600 hover:bg-purple-700 text-white'
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedOptimization(isSelected ? null : card.id);
                  }}
                >
                  {isAnalysisCompleted(card.id) ? 'Voir les résultats' : card.buttonText}
                </button>
              </div>
            );
          })}
        </div>

        {/* Zone d'affichage des détails */}
        {renderOptimizationDetails()}

      </div>

      {/* Modale Choix Final */}
      {showFinalChoiceModal && (
        <ChoixFinalPanel
          analysisSummary={analysisSummary}
          pdlId={pdlId}
          analysisYear={analysisYear}
          session={session}
          onClose={() => setShowFinalChoiceModal(false)}
          onSaved={async (updates) => {
            setAnalysisSummary(prev => ({ ...prev, ...updates }));
            // Bug du 24/10/2026 (LE BAO BAR) : le résumé partagé (montants du choix retenu) restait
            // celui calculé AVANT le choix — sans choix, la stratégie PS par défaut est celle qui
            // économise le plus (IA 658 €) → bandeau « manuel (658 €) », total 910 € au lieu de
            // 460 + 252 = 712 €. On relit le résumé recalculé par le serveur et on le propage.
            try {
              const r = await fetch(`/api/turpe/get-analysis-summary/${pdlId}`, { headers: { Authorization: `Bearer ${session.access_token}` } });
              const relu = (await lireReponseApi(r, 'Relecture du résumé'))?.data;
              if (relu) {
                setAnalysisSummary(relu);
                if (onAnalysisUpdated) onAnalysisUpdated(relu);
                return;
              }
            } catch (e) {
              console.error('[AnalyseTurpe] relecture du résumé après choix final', e);
              toast.error(`${e.message} — recharger la page pour voir les montants du choix retenu.`);
            }
            // Sans résumé relu, on n'affiche pas d'anciens montants : le résumé est retiré.
            setAnalysisSummary(prev => ({ ...prev, ...updates, resume: prev?.resume ? { ...prev.resume, economie_totale_eur: null } : prev?.resume }));
            if (onAnalysisUpdated) onAnalysisUpdated(updates);
          }}
        />
      )}
    </div>
  );
};

// ===== CHOIX FINAL : modale flottante avec sélection IA/Manuel + CU/LU =====
const ChoixFinalPanel = ({ analysisSummary, pdlId, analysisYear, session, onSaved, onClose }) => {
  const [chosenPs, setChosenPs] = useState(analysisSummary?.chosen_optimization || 'auto');
  const [chosenVoU, setChosenVoU] = useState(
    analysisSummary?.chosen_version_of_use ||
    analysisSummary?.recommended_version_of_use ||
    'CU'
  );
  const [saving, setSaving] = useState(false);

  const autoSaving = Number(analysisSummary?.best_saving_eur || 0);
  const manualSaving = Number(analysisSummary?.manual_saving_eur || 0);
  const recommendedVoU = analysisSummary?.recommended_version_of_use || 'CU';

  const formatCurrency = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Number(n) || 0);

  const currentChoiceMatches =
    chosenPs === (analysisSummary?.chosen_optimization || null) &&
    chosenVoU === (analysisSummary?.chosen_version_of_use || null);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch('/api/turpe/update-analysis-result', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({
          pdl_id: pdlId,
          analysis_type: 'set_chosen',
          analysis_year: analysisYear,
          result_data: { chosen_optimization: chosenPs, chosen_version_of_use: chosenVoU },
        }),
      });
      await lireReponseApi(res, 'Choix final');
      onSaved?.({ chosen_optimization: chosenPs, chosen_version_of_use: chosenVoU });
      toast.success('Choix final enregistré.');
      onClose?.();
    } catch (e) {
      toast.error(e.message || 'Échec sauvegarde du choix.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl border border-gray-200 w-[600px] max-w-[95vw] p-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-200">
          <h3 className="text-xs font-bold text-gray-800 flex items-center">
            <FiCheckCircle className="mr-1.5 h-3.5 w-3.5 text-emerald-600" />
            Choix final
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-sm">✕</button>
        </div>

        <p className="text-[10px] text-gray-600 mb-2">
          Sélectionne la stratégie de puissances souscrites et la version d'utilisation à retenir pour le contrat.
        </p>

        <div className="space-y-2">
          {/* Dimension 1 : stratégie PS */}
          <div className="border border-gray-200 rounded p-2">
            <p className="text-[10px] font-semibold text-gray-700 mb-1 uppercase tracking-wide">Stratégie puissances souscrites</p>
            <label className="flex items-start gap-2 p-1 cursor-pointer hover:bg-gray-50 rounded">
              <input type="radio" name="chosenPs" value="auto" checked={chosenPs === 'auto'} onChange={() => setChosenPs('auto')} className="mt-0.5" />
              <span className="text-[10px]">
                <span className="font-semibold">Optimisation IA</span> · gain estimé <span className="font-bold text-blue-600">{formatCurrency(autoSaving)}/an</span>
              </span>
            </label>
            <label className="flex items-start gap-2 p-1 cursor-pointer hover:bg-gray-50 rounded">
              <input type="radio" name="chosenPs" value="manual" checked={chosenPs === 'manual'} onChange={() => setChosenPs('manual')} className="mt-0.5" />
              <span className="text-[10px]">
                <span className="font-semibold">Optimisation manuelle</span> · gain estimé <span className="font-bold text-orange-600">{formatCurrency(manualSaving)}/an</span>
              </span>
            </label>
          </div>

          {/* Dimension 2 : version d'utilisation (CU / MU / LU) */}
          <div className="border border-gray-200 rounded p-2">
            <p className="text-[10px] font-semibold text-gray-700 mb-1 uppercase tracking-wide">
              Version d'utilisation
              {recommendedVoU && <span className="ml-1 text-[9px] font-normal text-gray-500 normal-case">(reco moteur : {recommendedVoU})</span>}
            </p>
            {(() => {
              const couts = analysisSummary?.version_of_use_costs || null;
              const currentVoU = analysisSummary?.current_version_of_use || null;
              const versionLabels = { CU: 'Courte Utilisation', MU: 'Moyenne Utilisation', LU: 'Longue Utilisation' };
              // Si on n'a pas les coûts par version (ancienne analyse), fallback
              // sur l'affichage simple CU/LU + saving global.
              const orderedKeys = couts ? ['CU', 'MU', 'LU'].filter(k => k in couts) : ['CU', 'LU'];
              const coutCurrent = couts && currentVoU ? Number(couts[currentVoU]) : null;
              return orderedKeys.map((key) => {
                const isCurrent = key === currentVoU;
                const isReco = key === recommendedVoU;
                let deltaNode = null;
                if (couts && coutCurrent !== null && !isCurrent) {
                  const delta = coutCurrent - Number(couts[key]); // >0 = éco si on passe à `key`
                  if (delta > 0) {
                    deltaNode = <span className="ml-1 font-bold text-green-600">éco {formatCurrency(delta)}/an</span>;
                  } else if (delta < 0) {
                    deltaNode = <span className="ml-1 text-red-600">surcoût {formatCurrency(Math.abs(delta))}/an</span>;
                  }
                }
                return (
                  <label key={key} className="flex items-start gap-2 p-1 cursor-pointer hover:bg-gray-50 rounded">
                    <input type="radio" name="chosenVoU" value={key} checked={chosenVoU === key} onChange={() => setChosenVoU(key)} className="mt-0.5" />
                    <span className="text-[10px]">
                      <span className="font-semibold">{key}</span> ({versionLabels[key] || key})
                      {isCurrent && <span className="ml-1 text-gray-500">(actuel)</span>}
                      {deltaNode}
                      {isReco && <span className="ml-1 text-emerald-700">✓ recommandé</span>}
                    </span>
                  </label>
                );
              });
            })()}
          </div>
        </div>

        <div className="flex items-center justify-end mt-3 gap-2">
          <button onClick={onClose} className="px-2 py-1 rounded text-[10px] border border-gray-300 text-gray-700 hover:bg-gray-50">
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={saving || currentChoiceMatches}
            className={`px-2 py-1 rounded text-[10px] flex items-center gap-1 transition-colors ${
              currentChoiceMatches
                ? 'bg-emerald-100 text-emerald-700 border border-emerald-300 cursor-default'
                : 'bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50'
            }`}
          >
            {saving ? <FiLoader className="animate-spin h-3 w-3" /> : <FiCheckCircle className="h-3 w-3" />}
            {currentChoiceMatches ? 'Choix déjà enregistré' : 'Valider le choix final'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ===== ONGLET OPTIMISATION STRATEGIQUE =====
const LoadShiftingAnalysis = ({ pdlData, session }) => {
  const [selectedCard, setSelectedCard] = useState(null);
  const [analysisResults, setAnalysisResults] = useState([]);
  const [loadingCardId, setLoadingCardId] = useState(null);
  const currentYear = new Date().getFullYear();
  const availableYears = [];
  for (let year = currentYear; year >= 2020; year--) {
    availableYears.push(year);
  }
  // L'état par défaut sera la dernière année COMPLÈTE
  const [analysisConfig, setAnalysisConfig] = useState({
    peaks_to_shift: 10,
    shift_percentage: 20,
    analysis_year: currentYear - 1 // Default to last full year
  });

  // Configuration des scénarios de déplacement de charge
  const loadShiftingCards = [
    {
      id: 'conservative',
      title: 'Déplacement Conservateur',
      subtitle: '5-10 pics, 15% déplacement',
      description: 'Approche prudente avec déplacement limité des pics de consommation',
      config: { peaks_to_shift: 5, shift_percentage: 15 }
    },
    {
      id: 'balanced',
      title: 'Déplacement Équilibré',
      subtitle: '10-15 pics, 25% déplacement',
      description: 'Stratégie équilibrée optimisant le rapport bénéfice/risque',
      config: { peaks_to_shift: 10, shift_percentage: 25 }
    },
    {
      id: 'aggressive',
      title: 'Déplacement Agressif',
      subtitle: '15-20 pics, 35% déplacement',
      description: 'Optimisation maximale avec déplacement important des pics',
      config: { peaks_to_shift: 15, shift_percentage: 35 }
    },
    {
      id: 'custom',
      title: 'Configuration Personnalisée',
      subtitle: 'Paramètres sur mesure',
      description: 'Définissez vos propres paramètres de déplacement',
      config: null
    }
  ];

  const runLoadShiftingAnalysis = async (config, cardId) => {
    setLoadingCardId(cardId);
    try {
      // 1. Créer un objet FormData
      const formData = new FormData();

      // 2. Ajouter chaque champ attendu par le backend
      formData.append('pdl_number', pdlData.pdl_number);
      formData.append('user_id', session.user.id);
      formData.append('analysis_year', analysisConfig.analysis_year);
      formData.append('num_peaks_to_shift', config.peaks_to_shift);
      // Le backend attend un float (ex: 0.25), pas un entier (25)
      formData.append('shift_percentage', config.shift_percentage / 100);

      // 3. Lancer le fetch
      const response = await fetch('/api/v4/analyze-load-shifting', {
        method: 'POST',
        // ❌ NE PAS METTRE 'Content-Type'. Le navigateur le fera pour vous.
        body: formData // ✅ Envoyer l'objet FormData directement
      });
      
      if (!response.ok) {
        // Pour un debug plus précis, on peut lire le message d'erreur du backend
        const errorData = await response.json();
        console.error("Détail de l'erreur 422:", errorData);
        throw new Error('Erreur lors de l\'analyse');
      }
      
      const result = await response.json();

      setAnalysisResults(prev => {
        //  On sauvegarde l'objet "à plat", sans la clé "analysis"
        const newResultObject = {
          ...result, // On étale toutes les clés du résultat (success, impact_analysis, etc.)
          id: cardId  // Et on ajoute l'ID de la carte
        };

        const existingIndex = prev.findIndex(r => r.id === cardId);
        if (existingIndex >= 0) {
          const updated = [...prev];
          updated[existingIndex] = newResultObject;
          return updated;
        }
        return [...prev, newResultObject];
      });
    } catch (error) {
      console.error('Erreur analyse déplacement:', error);
      toast.error("L'analyse du déplacement de charge a échoué.");
    } finally {
      setLoadingCardId(null);
    }
  };

  const handleCardClick = async (card) => {
    setSelectedCard(card.id);
    
    if (card.config) {
      await runLoadShiftingAnalysis(card.config, card.id);
    }
  };

  const handleCustomAnalysis = async () => {
    if (selectedCard === 'custom') {
      await runLoadShiftingAnalysis({
        peaks_to_shift: analysisConfig.peaks_to_shift,
        shift_percentage: analysisConfig.shift_percentage
      });
    }
  };

  const getCardResults = (cardId) => {
    return analysisResults.find(r => r.id === cardId);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { 
      style: 'currency', 
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="space-y-2">
      {/* En-tête dense */}
      <div className="bg-gray-50 border border-gray-200 rounded px-2 py-1.5 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[10px]">
          <h3 className="font-semibold text-gray-700 uppercase tracking-wide">Peak Shaving</h3>
          <span className="text-gray-300">|</span>
          <span className="text-gray-500">Potentiel de déplacement de charge</span>
        </div>
        <div className="flex items-center gap-1.5">
          <label htmlFor="analysis-year-select" className="text-[10px] text-gray-500 uppercase tracking-wide">Année</label>
          <select
            id="analysis-year-select"
            value={analysisConfig.analysis_year}
            onChange={(e) => setAnalysisConfig(prev => ({...prev, analysis_year: parseInt(e.target.value)}))}
            className="border border-gray-300 rounded px-1.5 py-0.5 text-[10px] focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {availableYears.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Grille des cartes de stratégies */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        {loadShiftingCards.map((card) => {
          const results = getCardResults(card.id);
          const isSelected = selectedCard === card.id;
          const hasResults = results && results.impact_analysis;

          return (
            <div
              key={card.id}
              onClick={() => handleCardClick(card)}
              className={`
                bg-white rounded border-2 cursor-pointer transition-all duration-200 p-2
                ${isSelected
                  ? 'border-blue-500 shadow-md bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                }
              `}
            >
              <div className="flex items-start justify-between gap-1">
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-gray-900 text-[11px] leading-tight">{card.title}</h4>
                  <p className="text-[10px] text-gray-600 mt-0.5">{card.subtitle}</p>
                  <p className="text-[9px] text-gray-500 mt-0.5 line-clamp-2 leading-tight">{card.description}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  {hasResults && (
                  <div>
                    <div className="text-[10px] font-bold text-green-600 leading-tight">
                      {formatCurrency(results.impact_analysis.potential_annual_saving_eur)}
                    </div>
                    <div className="text-[9px] text-gray-500">/an</div>
                  </div>
                  )}
                  {loadingCardId === card.id && (
                    <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Configuration personnalisée */}
      {selectedCard === 'custom' && (
        <div className="bg-white rounded border border-gray-200 p-2">
          <h4 className="font-semibold text-gray-700 mb-1.5 text-[10px] uppercase tracking-wide">Configuration Personnalisée</h4>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="block text-[10px] font-medium text-gray-600 mb-0.5">
                Nombre de pics à déplacer
              </label>
              <input
                type="number"
                min="1"
                max="50"
                value={analysisConfig.peaks_to_shift}
                onChange={(e) => setAnalysisConfig(prev => ({...prev, peaks_to_shift: parseInt(e.target.value)}))}
                className="w-full border border-gray-300 rounded px-2 py-1 text-[10px] focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-gray-600 mb-0.5">
                Pourcentage de déplacement (%)
              </label>
              <input
                type="number"
                min="5"
                max="50"
                value={analysisConfig.shift_percentage}
                onChange={(e) => setAnalysisConfig(prev => ({...prev, shift_percentage: parseInt(e.target.value)}))}
                className="w-full border border-gray-300 rounded px-2 py-1 text-[10px] focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={handleCustomAnalysis}
                disabled={isLoading}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-2 py-1 rounded text-[10px] font-medium transition-colors"
              >
                {isLoading ? 'Analyse...' : 'Analyser'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Détails de l'analyse sélectionnée */}
      {/* Détails de l'analyse sélectionnée */}
      {selectedCard && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-4 py-3 border-b border-gray-200">
            <h4 className="font-medium text-gray-900">
              Résultats - {loadShiftingCards.find(c => c.id === selectedCard)?.title}
            </h4>
          </div>
          
          {(() => {
            const resultForCard = getCardResults(selectedCard);

            // CAS 1 : L'ANALYSE EST EN COURS DE CHARGEMENT
            if (loadingCardId === selectedCard) {
              return (
                <div className="p-6 text-center">
                  <FiLoader className="h-6 w-6 text-blue-600 animate-spin mx-auto" />
                  <p className="mt-2 text-xs text-gray-600">Analyse en cours...</p>
                </div>
              );
            }

            // CAS 2 : L'ANALYSE EST TERMINÉE, MAIS AUCUN PIC PERTINENT N'A ÉTÉ TROUVÉ
            // On utilise l'optional chaining (?.) pour éviter les erreurs si resultForCard est undefined
            if (resultForCard?.status === 'NO_SIGNIFICANT_PEAKS') {
              return (
                <div className="p-6 text-center">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 inline-block">
                    <FiInfo className="h-8 w-8 text-blue-500 mx-auto mb-3" />
                    <h5 className="font-semibold text-blue-800">Aucun Pic Pertinent Trouvé</h5>
                    <p className="text-xs text-blue-700 mt-1 max-w-md mx-auto">
                      {resultForCard.message}
                    </p>
                  </div>
                </div>
              );
            }

            // CAS 3 : L'ANALYSE A RÉUSSI ET A TROUVÉ DES RÉSULTATS
            const analysis = resultForCard?.impact_analysis;
            if (analysis) {
              const totalEnergyShifted = (resultForCard.shifted_logs || []).reduce((sum, log) => sum + log.energy_kwh, 0);

              return (
                <div className="p-4 space-y-6">
                  {/* Métriques principales */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center p-3 bg-green-50 rounded-lg border border-green-200">
                      <div className="text-xs font-bold text-green-600">
                        {formatCurrency(analysis.potential_annual_saving_eur)}
                      </div>
                      <div className="text-xs text-gray-600">Économie Totale Annuelle</div>
                    </div>
                    <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="text-xs font-bold text-blue-600">
                        {(resultForCard.top_peaks_identified || []).length}
                      </div>
                      <div className="text-xs text-gray-600">Pics Déplacés</div>
                    </div>
                    <div className="text-center p-3 bg-purple-50 rounded-lg border border-purple-200">
                      <div className="text-xs font-bold text-purple-600">
                        {totalEnergyShifted.toFixed(0)} kWh
                      </div>
                      <div className="text-xs text-gray-600">Énergie Déplacée</div>
                    </div>
                  </div>

                  {/* Détail des économies */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h5 className="font-medium text-gray-900 mb-3 text-xs">Répartition des Économies</h5>
                      <div className="space-y-2 text-xs">
                        <div className="flex justify-between items-center py-2 bg-gray-50 px-3 rounded">
                          <span>Part Variable (kWh) :</span>
                          <span className="font-medium text-green-700">
                            {formatCurrency(analysis.saving_from_variable_part_eur)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-2 bg-gray-50 px-3 rounded">
                          <span>Part Fixe (Abonnement) :</span>
                          <span className="font-medium text-green-700">
                            {formatCurrency(analysis.estimated_saving_from_fixed_part_eur)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-2 px-3 rounded bg-green-100 border-t border-green-200">
                          <span className="font-bold">Total :</span>
                          <span className="font-bold text-green-800">
                            {formatCurrency(analysis.potential_annual_saving_eur)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div>
                        <h5 className="font-medium text-gray-900 mb-3 text-xs">Impact sur la Puissance</h5>
                        <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                            <p className="text-xs text-orange-700">Réduction potentielle de la puissance souscrite en Heures Pleines :</p>
                            <p className="text-center text-xs font-bold text-orange-800 mt-2">
                                - {analysis.potential_power_reduction_kva.toFixed(1)} kVA
                            </p>
                        </div>
                    </div>
                  </div>

                  {/* Recommandations */}
                  {resultForCard.recommendation && (
                    <div>
                      <h5 className="font-medium text-gray-900 mb-2 text-xs">Recommandation</h5>
                      <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                        <p className="text-xs text-blue-800">
                           {resultForCard.recommendation}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              );
            }

            // CAS 4 : Par défaut, si rien n'est encore prêt
            return (
              <div className="p-6 text-gray-500 text-xs text-center">
                <p>Cliquez sur un scénario pour lancer l'analyse.</p>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};

// ===== COMPOSANT OPTIMISATION ÉNERGIE RÉACTIVE =====
const ReactiveEnergyAnalysis = ({ pdlData, session }) => {
  const [selectedCard, setSelectedCard] = useState(null);
  const [analysisResults, setAnalysisResults] = useState([]);
  const [loadingCardId, setLoadingCardId] = useState(null);
  const currentYear = new Date().getFullYear();
  const availableYears = [];
  for (let year = currentYear; year >= 2020; year--) {
    availableYears.push(year);
  }
  
  const [analysisConfig, setAnalysisConfig] = useState({
    analysis_year: currentYear - 1,
    target_tan_phi: 0.4,
    battery_cost_per_kvar: 50
  });

  // Configuration des scénarios d'optimisation énergie réactive
  const reactiveEnergyCards = [
    {
      id: 'standard',
      title: 'Compensation Standard',
      subtitle: 'tan φ = 0.4, 50€/kVAr',
      description: 'Solution de compensation standard conforme aux exigences réglementaires',
      config: { target_tan_phi: 0.4, battery_cost_per_kvar: 50 },
      icon: '🔋',
      color: 'blue'
    },
    {
      id: 'optimized',
      title: 'Compensation Optimisée',
      subtitle: 'tan φ = 0.3, 45€/kVAr',
      description: 'Optimisation poussée avec équipements haute performance',
      config: { target_tan_phi: 0.3, battery_cost_per_kvar: 45 },
      icon: '⚡',
      color: 'green'
    },
    {
      id: 'premium',
      title: 'Compensation Premium',
      subtitle: 'tan φ = 0.25, 60€/kVAr',
      description: 'Solution haut de gamme avec régulation dynamique',
      config: { target_tan_phi: 0.25, battery_cost_per_kvar: 60 },
      icon: '💎',
      color: 'purple'
    },
    {
      id: 'custom',
      title: 'Configuration Personnalisée',
      subtitle: 'Paramètres sur mesure',
      description: 'Définissez vos propres paramètres de compensation',
      config: null,
      icon: '⚙️',
      color: 'orange'
    }
  ];

  const runReactiveEnergyAnalysis = async (config, cardId) => {
    setLoadingCardId(cardId);
    try {
      const formData = new FormData();
      formData.append('pdl_number', pdlData.pdl_number);
      formData.append('user_id', session.user.id);
      formData.append('analysis_year', analysisConfig.analysis_year);
      formData.append('target_tan_phi', config.target_tan_phi);
      formData.append('battery_cost_per_kvar', config.battery_cost_per_kvar);

      const response = await fetch('/api/v4/analyze-reactive-energy', {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error("Détail de l'erreur:", errorData);
        throw new Error('Erreur lors de l\'analyse');
      }
      
      const result = await response.json();

      setAnalysisResults(prev => {
        const newResultObject = {
          ...result,
          id: cardId
        };

        const existingIndex = prev.findIndex(r => r.id === cardId);
        if (existingIndex >= 0) {
          const updated = [...prev];
          updated[existingIndex] = newResultObject;
          return updated;
        }
        return [...prev, newResultObject];
      });
    } catch (error) {
      console.error('Erreur analyse énergie réactive:', error);
      toast.error("L'analyse de l'énergie réactive a échoué.");
    } finally {
      setLoadingCardId(null);
    }
  };

  const handleCardClick = async (card) => {
    setSelectedCard(card.id);
    
    if (card.config) {
      await runReactiveEnergyAnalysis(card.config, card.id);
    }
  };

  const handleCustomAnalysis = async () => {
    if (selectedCard === 'custom') {
      await runReactiveEnergyAnalysis({
        target_tan_phi: analysisConfig.target_tan_phi,
        battery_cost_per_kvar: analysisConfig.battery_cost_per_kvar
      }, 'custom');
    }
  };

  const getCardResults = (cardId) => {
    return analysisResults.find(r => r.id === cardId);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { 
      style: 'currency', 
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="space-y-2">
      {/* En-tête dense */}
      <div className="bg-gray-50 border border-gray-200 rounded px-2 py-1.5 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[10px]">
          <h3 className="font-semibold text-gray-700 uppercase tracking-wide">Énergie Réactive</h3>
          <span className="text-gray-300">|</span>
          <span className="text-gray-500">Pénalités &amp; compensation réactive</span>
        </div>
        <div className="flex items-center gap-1.5">
          <label htmlFor="reactive-year-select" className="text-[10px] text-gray-500 uppercase tracking-wide">Année</label>
          <select
            id="reactive-year-select"
            value={analysisConfig.analysis_year}
            onChange={(e) => setAnalysisConfig(prev => ({...prev, analysis_year: parseInt(e.target.value)}))}
            className="border border-gray-300 rounded px-1.5 py-0.5 text-[10px] focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {availableYears.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Grille des cartes de solutions */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        {reactiveEnergyCards.map((card) => {
          const results = getCardResults(card.id);
          const isSelected = selectedCard === card.id;
          const hasResults = results?.status === 'OPTIMIZATION_FOUND';

          return (
            <div
              key={card.id}
              onClick={() => handleCardClick(card)}
              className={`
                bg-white rounded border-2 cursor-pointer transition-all duration-200 p-2
                ${isSelected
                  ? `border-${card.color}-500 shadow-md bg-${card.color}-50`
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                }
              `}
            >
              <div className="flex items-start justify-between gap-1">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="text-sm">{card.icon}</span>
                    <h4 className="font-semibold text-gray-900 text-[11px] leading-tight">{card.title}</h4>
                  </div>
                  <p className="text-[10px] text-gray-600">{card.subtitle}</p>
                  <p className="text-[9px] text-gray-500 line-clamp-2 leading-tight mt-0.5">{card.description}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  {hasResults && (
                    <div>
                      <div className="text-[10px] font-bold text-green-600 leading-tight">
                        {formatCurrency(results.solution_proposal.potential_annual_saving_eur)}
                      </div>
                      <div className="text-[9px] text-gray-500">/an</div>
                    </div>
                  )}
                  {loadingCardId === card.id && (
                    <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Configuration personnalisée */}
      {selectedCard === 'custom' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <h4 className="font-medium text-gray-900 mb-3">Configuration Personnalisée</h4>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                tan φ cible
              </label>
              <input
                type="number"
                min="0.1"
                max="1"
                step="0.05"
                value={analysisConfig.target_tan_phi}
                onChange={(e) => setAnalysisConfig(prev => ({...prev, target_tan_phi: parseFloat(e.target.value)}))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Coût batterie (€/kVAr)
              </label>
              <input
                type="number"
                min="20"
                max="100"
                value={analysisConfig.battery_cost_per_kvar}
                onChange={(e) => setAnalysisConfig(prev => ({...prev, battery_cost_per_kvar: parseInt(e.target.value)}))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={handleCustomAnalysis}
                disabled={loadingCardId === 'custom'}
                className="w-full bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-md text-xs font-medium transition-colors"
              >
                {loadingCardId === 'custom' ? 'Analyse...' : 'Analyser'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Détails de l'analyse sélectionnée */}
      {selectedCard && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-4 py-3 border-b border-gray-200">
            <h4 className="font-medium text-gray-900">
              Résultats - {reactiveEnergyCards.find(c => c.id === selectedCard)?.title}
            </h4>
          </div>
          
          {(() => {
            const resultForCard = getCardResults(selectedCard);

            if (loadingCardId === selectedCard) {
              return (
                <div className="p-6 text-center">
                  <FiLoader className="h-6 w-6 text-blue-600 animate-spin mx-auto" />
                  <p className="mt-2 text-xs text-gray-600">Analyse en cours...</p>
                </div>
              );
            }

            if (resultForCard?.status === 'ALREADY_OPTIMAL' || resultForCard?.status === 'NO_REACTIVE_DATA') {
              return (
                <div className="p-6 text-center">
                  <div className="bg-green-50 ...">
                    <FiCheckCircle className="h-8 w-8 text-green-500 ..."/>
                    <h5 className="font-semibold text-green-800">
                      {resultForCard.status === 'ALREADY_OPTIMAL' ? 'Aucune Pénalité Détectée' : 'Données Manquantes'}
                    </h5>
                    <p className="text-xs text-green-700 mt-1 ...">
                      {resultForCard.message}
                    </p>
                  </div>
                </div>
              );
            }

            const solution = resultForCard?.solution_proposal;
            const summary = resultForCard?.analysis_summary;
            if (solution && summary) {
              return (
                <div className="p-4 space-y-6">
                  {/* Métriques principales */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-red-50 rounded-lg border border-red-200">
                      <div className="text-xs font-bold text-red-600">
                        {formatCurrency(summary.annual_cost_eur)}
                      </div>
                      <div className="text-xs text-gray-600">Pénalité Annuelle Actuelle</div>
                    </div>
                    <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="text-xs font-bold text-blue-600">
                        {solution.recommended_capacitor_bank_kvar} kVAr
                      </div>
                      <div className="text-xs text-gray-600">Batterie Recommandée</div>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded-lg border border-green-200">
                      <div className="text-xs font-bold text-green-600">
                        {formatCurrency(solution.potential_annual_saving_eur)}
                      </div>
                      <div className="text-xs text-gray-600">Économie Annuelle</div>
                    </div>
                    <div className="text-center p-3 bg-purple-50 rounded-lg border border-purple-200">
                      <div className="text-xs font-bold text-purple-600">
                        {solution.return_on_investment_months} mois
                      </div>
                      <div className="text-xs text-gray-600">Retour sur Investissement</div>
                    </div>
                  </div>

                  {/* Analyse détaillée */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Détail de la solution */}
                    <div>
                      <h5 className="font-medium text-gray-900 mb-3 text-xs">Solution Technique Recommandée</h5>
                      <div className="space-y-3">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-xs font-medium text-blue-800">Batterie de Condensateurs</span>
                            <span className="text-xs font-bold ...">{solution.recommended_capacitor_bank_kvar} kVAr</span>
                            </div>
                            <div className="text-xs text-blue-700 space-y-1">
                              <p>• Coût d'installation: {formatCurrency(solution.estimated_investment_eur)}</p>
                              <p>• tan φ cible: {analysisConfig.target_tan_phi}</p>
                              <p>• Réduction de la pénalité: {((solution.potential_annual_saving_eur / summary.annual_cost_eur) * 100).toFixed(1)}%</p>
                            </div>
                        </div>
                      </div>
                    </div>

                    {/* ROI et rentabilité */}
                    <div>
                      <h5 className="font-medium text-gray-900 mb-3 text-xs">Analyse de Rentabilité</h5>
                      <div className="space-y-3">
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                          <div className="text-xs space-y-2">
                            <div className="flex justify-between">
                              <span>Investissement initial:</span>
                              <span className="font-medium">{formatCurrency(analysis.installation_cost_eur)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Économie mensuelle:</span>
                              <span className="font-medium text-green-700">{formatCurrency(analysis.annual_savings_eur / 12)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Économie annuelle:</span>
                              <span className="font-medium text-green-700">{formatCurrency(analysis.annual_savings_eur)}</span>
                            </div>
                            <div className="border-t pt-2 flex justify-between">
                              <span className="font-bold">ROI:</span>
                              <span className="font-bold text-purple-700">{analysis.roi_months} mois</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Recommandations */}
                  {resultForCard.recommendation && (
                    <div>
                      <h5 className="font-medium text-gray-900 mb-2 text-xs">Recommandations</h5>
                      <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-200">
                        <ul className="text-xs text-yellow-800 space-y-1">
                          {resultForCard.recommendations.map((rec, index) => (
                            <li key={index}>• {rec}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* Détails techniques */}
                  {resultForCard.technical_details && (
                    <div>
                      <h5 className="font-medium text-gray-900 mb-2 text-xs">Détails Techniques</h5>
                      <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                        <div className="grid grid-cols-2 gap-4 text-xs">
                          <div>
                            <span className="text-gray-600">tan φ max détecté:</span>
                            <span className="ml-2 font-mono">{resultForCard.technical_details.max_tan_phi_detected?.toFixed(3) || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Période critique:</span>
                            <span className="ml-2 font-mono">{resultForCard.technical_details.critical_period || 'N/A'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            }

            return (
              <div className="p-6 text-gray-500 text-xs text-center">
                <p>Cliquez sur une solution pour lancer l'analyse.</p>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};

// ===== VUE GLOBALE TURPE (format aligné AnalyseCspe) =====
const FileDot = ({ ok, label }) => (
  <span
    title={label}
    className={`inline-block w-2.5 h-2.5 rounded-full border transition-colors ${
      ok ? 'bg-emerald-400 border-emerald-500' : 'bg-gray-200 border-gray-300'
    }`}
  />
);

const TurpeGlobalView = ({ session, navigate }) => {
  const { data: pdls, degraded, warnings, loading, error, refetch } = useOptimizablePDLs();
  // Année par défaut = dernière année complète (l'engine TURPE refuse les
  // analyses < 330 jours, donc l'année courante n'est jamais valide).
  const currentYear = new Date().getFullYear() - 1;
  const availableYears = [2023, 2024, 2025, 2026, 2027];

  const [selectedYear, setSelectedYear]       = useState(currentYear);
  const [selectedState, setSelectedState]     = useState('');
  const [search, setSearch]                   = useState('');
  const [showFilters, setShowFilters]         = useState(false);
  const [sortConfig, setSortConfig]           = useState({ key: 'company_name', direction: 'asc' });
  const [expandedAccounts, setExpandedAccounts] = useState(new Set());

  const toggleAccount = (accountId) => {
    setExpandedAccounts(prev => {
      const next = new Set(prev);
      if (next.has(accountId)) next.delete(accountId);
      else next.add(accountId);
      return next;
    });
  };

  const handleSort = (key) =>
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));

  const SortableHeader = ({ label, colKey, className = '' }) => (
    <th
      onClick={() => handleSort(colKey)}
      className={`px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500
                  uppercase tracking-wider cursor-pointer select-none hover:bg-gray-100 ${className}`}
    >
      <span className="flex items-center gap-1">
        {label}
        {sortConfig.key === colKey
          ? sortConfig.direction === 'asc'
            ? <FiChevronUp   className="w-3 h-3 text-teal-600" />
            : <FiChevronDown className="w-3 h-3 text-teal-600" />
          : <FiChevronDown className="w-3 h-3 text-gray-300" />}
      </span>
    </th>
  );

  const rows = useMemo(() => {
    let data = [...pdls];

    if (search) {
      const q = search.toLowerCase();
      data = data.filter(p =>
        (p.company_name || '').toLowerCase().includes(q) ||
        (p.pdl_number  || '').toLowerCase().includes(q) ||
        (p.siret       || '').toLowerCase().includes(q)
      );
    }

    if (selectedState === '100') {
      data = data.filter(p => p.progress_pct === 100);
    } else if (selectedState === 'partial') {
      data = data.filter(p => p.progress_pct > 0 && p.progress_pct < 100);
    } else if (selectedState === '0') {
      data = data.filter(p => p.progress_pct === 0);
    }

    data.sort((a, b) => {
      const av = a[sortConfig.key] ?? '';
      const bv = b[sortConfig.key] ?? '';
      const cmp = String(av).localeCompare(String(bv), 'fr', { numeric: true });
      return sortConfig.direction === 'asc' ? cmp : -cmp;
    });

    return data;
  }, [pdls, search, selectedState, sortConfig]);

  const accountCount = useMemo(
    () => new Set(rows.map(p => p.account_id).filter(Boolean)).size,
    [rows]
  );

  // Groupement par account : 1 ligne par client + N lignes PDL enfants
  const accountGroups = useMemo(() => {
    const map = new Map();
    for (const pdl of rows) {
      if (!map.has(pdl.account_id)) {
        map.set(pdl.account_id, {
          account_id: pdl.account_id,
          company_name: pdl.company_name,
          siret: pdl.siret,
          commercial_name: pdl.commercial_name,
          opportunity_id: pdl.opportunity_id,
          pdls: [],
        });
      }
      map.get(pdl.account_id).pdls.push(pdl);
    }
    const list = Array.from(map.values());
    list.forEach(g => {
      g.pdls.sort((a, b) => b.progress_pct - a.progress_pct);
    });
    list.sort((a, b) => {
      const aMax = Math.max(...a.pdls.map(p => p.progress_pct));
      const bMax = Math.max(...b.pdls.map(p => p.progress_pct));
      if (aMax !== bMax) return bMax - aMax;
      return (a.company_name || '').localeCompare(b.company_name || '', 'fr');
    });
    return list;
  }, [rows]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">

      {/* En-tête */}
      <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h2 className="text-xs font-semibold text-gray-800 flex items-center gap-1.5">
            PDL connectables — Acheminement TURPE
            <span className="px-1.5 py-0.5 bg-teal-50 text-teal-700 rounded text-[9px] font-medium">
              {selectedYear}
            </span>
            {selectedState && (
              <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[9px]">
                {selectedState === '100' ? '100% Prêts' : selectedState === 'partial' ? 'Partiels' : 'À connecter'}
              </span>
            )}
          </h2>
          <p className="text-[9px] text-gray-400 mt-0.5">
            {loading
              ? 'Chargement…'
              : `${accountCount} compte${accountCount !== 1 ? 's' : ''} · ${rows.length} PDL`}
          </p>
        </div>

        <div className="flex items-center gap-1.5">
          {/* Filtre état */}
          <select
            value={selectedState}
            onChange={e => setSelectedState(e.target.value)}
            className="py-1 px-1.5 border border-gray-200 rounded text-[10px] focus:outline-none focus:ring-1 focus:ring-teal-400"
          >
            <option value="">Tous les états</option>
            <option value="100">100% Prêts</option>
            <option value="partial">Partiels</option>
            <option value="0">À connecter</option>
          </select>

          {/* Filtre année */}
          <select
            value={selectedYear}
            onChange={e => setSelectedYear(parseInt(e.target.value))}
            className="py-1 px-1.5 border border-gray-200 rounded text-[10px] focus:outline-none focus:ring-1 focus:ring-teal-400"
          >
            {availableYears.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>

          {/* Recherche */}
          <div className="relative">
            <FiSearch className="absolute left-1.5 top-1/2 -translate-y-1/2 w-2.5 h-2.5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-5 pr-2 py-1 border border-gray-200 rounded text-[10px] w-32
                         focus:outline-none focus:ring-1 focus:ring-teal-400"
            />
          </div>

          {/* Bouton Filtres */}
          <button
            onClick={() => setShowFilters(p => !p)}
            className={`px-1.5 py-1 rounded text-[10px] border flex items-center gap-1 transition-colors ${
              showFilters
                ? 'bg-teal-50 border-teal-300 text-teal-700'
                : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
            }`}
          >
            <FiFilter className="w-2.5 h-2.5" /> Filtres
          </button>

          {/* Reset */}
          {(search || selectedState) && (
            <button
              onClick={() => { setSearch(''); setSelectedState(''); }}
              className="px-1.5 py-1 text-[10px] text-gray-500 hover:text-red-500
                         border border-gray-200 rounded"
            >✕ Reset</button>
          )}

          {/* Refresh */}
          <button
            onClick={refetch}
            disabled={loading}
            title="Rafraîchir"
            className="inline-flex items-center justify-center w-6 h-6 rounded bg-gray-50
                       border border-gray-200 text-gray-500 hover:bg-gray-100 disabled:opacity-40"
          >
            <FiRefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Panneau filtres avancés */}
      {showFilters && (
        <div className="px-2 py-1 bg-gray-50 border-b border-gray-100 flex flex-wrap gap-1.5 items-center">
          <span className="text-[10px] text-gray-500">Fichiers reçus :</span>
          {[
            { label: 'C68', key: 'has_c68' },
            { label: 'R63', key: 'has_r63' },
            { label: 'R65', key: 'has_r65' },
            { label: 'R67', key: 'has_r67' },
          ].map(f => (
            <span key={f.key} className="text-[10px] text-gray-600 bg-white border border-gray-200 rounded px-1.5 py-0.5">
              {f.label} : {rows.filter(p => p[f.key]).length} / {rows.length}
            </span>
          ))}
        </div>
      )}

      {/* Erreur */}
      {error && (
        <div className="px-3 py-2 bg-red-50 border-b border-red-100 text-[10px] text-red-600">
          Erreur de chargement : {error.message || String(error)}
        </div>
      )}
      {!loading && !error && (
        <EnedisDegradedBanner degraded={degraded} warnings={warnings} onRetry={refetch} className="border-x-0 border-t-0" />
      )}
      {!loading && !error && Array.isArray(degraded) && degraded.includes('turpe_analyses') && (
        <div role="alert" data-testid="turpe-analyses-degraded" className="flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
          <FiAlertTriangle className="h-3.5 w-3.5 flex-shrink-0" aria-hidden />
          <span>{(warnings || []).find(w => w.startsWith('Analyses TURPE')) || 'Analyses TURPE illisibles.'}</span>
          <button type="button" onClick={refetch} className="ml-auto underline whitespace-nowrap">Réessayer</button>
        </div>
      )}

      {/* Tableau */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-2 py-1.5 w-6"></th>
              <SortableHeader label="Client"   colKey="company_name" />
              <SortableHeader label="SIRET"    colKey="siret"        />
              <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider">PDL</th>
              <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider">C68</th>
              <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider">R63</th>
              <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider">R65</th>
              <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider">R67</th>
              <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider">État</th>
              <th className="px-2 py-1.5 text-center text-[10px] font-semibold text-gray-500 uppercase tracking-wider" title={`Analyse TURPE faite pour ${selectedYear} (gris : autre année)`}>Analysé</th>
              <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Optimisation proposée</th>
              <th className="px-2 py-1.5 text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Éco./an</th>
              <th className="px-2 py-1.5 text-center text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Analyse</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan={13} className="px-3 py-6 text-center text-[10px] text-gray-400">
                  <FiRefreshCw className="inline w-3.5 h-3.5 animate-spin mr-1" />
                  Chargement des PDL…
                </td>
              </tr>
            ) : accountGroups.length === 0 ? (
              <tr>
                <td colSpan={13} className="px-3 py-6 text-center text-[10px] text-gray-400">
                  Aucun client analysable trouvé
                </td>
              </tr>
            ) : (
              accountGroups.map(group => {
                const expanded = expandedAccounts.has(group.account_id);
                const initials = (group.company_name || '?')
                  .split(' ').filter(Boolean).slice(0, 2)
                  .map(w => w[0].toUpperCase()).join('');
                const total = group.pdls.length;
                const ready = group.pdls.filter(p => p.progress_pct === 100).length;
                const minPct = Math.min(...group.pdls.map(p => p.progress_pct));
                const allReady = ready === total && total > 0;
                const turpeCompte = totauxCompte(group.pdls, selectedYear);

                return (
                  <React.Fragment key={group.account_id}>
                    {/* Ligne compte */}
                    <tr
                      onClick={() => toggleAccount(group.account_id)}
                      className="cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-2 py-1.5 text-gray-400">
                        {expanded ? <FiChevronDown className="w-3 h-3" /> : <FiChevronRight className="w-3 h-3" />}
                      </td>
                      <td className="px-2 py-1.5">
                        <div className="flex items-center gap-1.5">
                          <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-blue-100 text-blue-700 text-[9px] font-semibold flex-shrink-0">
                            {initials}
                          </span>
                          <p className="text-[10px] font-semibold text-gray-900 leading-tight">
                            {group.company_name || <span className="text-gray-400 italic">#{group.account_id?.slice(0, 8)}</span>}
                          </p>
                        </div>
                      </td>
                      <td className="px-2 py-1.5 text-[10px] text-gray-500 font-mono">{group.siret || '—'}</td>
                      <td className="px-2 py-1.5 text-[10px] text-gray-500">{total} PDL{total > 1 ? 's' : ''}</td>
                      <td className="px-2 py-1.5 text-[10px] text-gray-400" colSpan={4}>
                        {ready}/{total} fichiers complets
                      </td>
                      <td className="px-2 py-1.5">
                        <PDLProgressBadge pct={minPct} />
                      </td>
                      <td className="px-2 py-1.5 text-center text-[9px] text-gray-500" title={`PDL à analyse concluante pour ${selectedYear}`}>
                        {turpeCompte.inconnu ? '?' : `${turpeCompte.analyses}/${total}`}
                      </td>
                      <td className="px-2 py-1.5"></td>
                      <td className="px-2 py-1.5 text-right text-[10px] font-semibold text-emerald-700 whitespace-nowrap">
                        {turpeCompte.analyses > 0 ? euros(turpeCompte.economie) : ''}
                      </td>
                      <td className="px-2 py-1.5 text-center text-[9px] text-gray-400">
                        {allReady ? `${ready}/${total} prêts` : '—'}
                      </td>
                    </tr>

                    {/* Lignes PDL enfants */}
                    {expanded && group.pdls.map(pdl => {
                      const canAnalyse = pdl.progress_pct === 100;
                      const resumeTurpe = choisirResume(pdl.turpe_analyses, selectedYear)?.resume || null;
                      return (
                        <tr key={pdl.pdl_id} className="bg-gray-50/40 hover:bg-gray-50 transition-colors text-[10px]">
                          <td className="px-2 py-1.5"></td>
                          <td className="px-2 py-1.5 pl-6 text-gray-600 italic" colSpan={2}>
                            ↳ {pdl.site_name || '—'}
                          </td>
                          <td className="px-2 py-1.5 text-gray-600 font-mono">
                            {pdl.pdl_number
                              ? (pdl.pdl_number.length > 14 ? pdl.pdl_number.slice(0, 14) + '…' : pdl.pdl_number)
                              : '—'}
                          </td>
                          <td className="px-2 py-1.5">
                            <FileDot ok={pdl.has_c68} label={pdl.has_c68 ? 'C68 reçu' : 'C68 manquant'} />
                          </td>
                          <td className="px-2 py-1.5">
                            <FileDot ok={pdl.has_r63} label={pdl.has_r63 ? 'R63 reçu' : 'R63 manquant'} />
                          </td>
                          <td className="px-2 py-1.5">
                            <FileDot ok={pdl.has_r65} label={pdl.has_r65 ? 'R65 reçu' : 'R65 manquant'} />
                          </td>
                          <td className="px-2 py-1.5">
                            <FileDot ok={pdl.has_r67} label={pdl.has_r67 ? 'R67 reçu' : 'R67 manquant'} />
                          </td>
                          <td className="px-2 py-1.5">
                            <PDLProgressBadge pct={pdl.progress_pct} unknown={!!pdl.enedis_flags_unknown} />
                          </td>
                          <td className="px-2 py-1.5 text-center">
                            <CocheAnalyse analyses={pdl.turpe_analyses} annee={selectedYear} />
                          </td>
                          <CellulesOptimisation analyses={pdl.turpe_analyses} annee={selectedYear} />
                          <td className="px-2 py-1.5 text-center">
                            <div className="inline-flex items-center gap-1">
                              {canAnalyse ? (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/dashboard/admin/analyse-turpe/${pdl.pdl_id}`, { state: { pdl, year: selectedYear } });
                                  }}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 text-[10px] font-medium transition-colors border border-blue-200"
                                >
                                  <FiBarChart2 className="w-3 h-3" /> Analyse
                                </button>
                              ) : (
                                <span className="text-[9px] text-gray-300">—</span>
                              )}
                              <BoutonRapportTurpe rapport={resumeTurpe?.rapport} session={session} />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ===== ONGLET AUDIT ENEDIS =====
const SEVERITY_STYLES = {
  error: {
    border: 'border-l-red-600',
    bg: 'bg-red-50',
    badge: 'bg-red-100 text-red-700',
    icon: FiXCircle,
    iconColor: 'text-red-600',
    label: 'Anomalie',
  },
  warning: {
    border: 'border-l-amber-600',
    bg: 'bg-amber-50',
    badge: 'bg-amber-100 text-amber-700',
    icon: FiAlertTriangle,
    iconColor: 'text-amber-600',
    label: 'Avertissement',
  },
  info: {
    border: 'border-l-blue-600',
    bg: 'bg-blue-50',
    badge: 'bg-blue-100 text-blue-700',
    icon: FiInfo,
    iconColor: 'text-blue-600',
    label: 'Info',
  },
};

const formatEur = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(value));
};

// ─── Status finding (persistence workflow) ─────────────────────────────────
// Mappings alignés sur le backend : enum (open, contested, resolved, refunded, dismissed).
const FINDING_STATUS_META = {
  open:                  { label: 'Ouvert',           bg: 'bg-gray-100',    text: 'text-gray-700',    border: 'border-gray-300'    },
  contested:             { label: 'Contesté',         bg: 'bg-orange-100',  text: 'text-orange-700',  border: 'border-orange-300'  },
  resolved:              { label: 'Résolu',           bg: 'bg-green-100',   text: 'text-green-700',   border: 'border-green-300'   },
  refunded:              { label: 'Remboursé',        bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-300' },
  dismissed:             { label: 'Écarté',           bg: 'bg-gray-100',    text: 'text-gray-500',    border: 'border-gray-200'    },
  confirmed_by_invoice:  { label: 'Confirmé',         bg: 'bg-blue-100',    text: 'text-blue-700',    border: 'border-blue-300'    },
};

// Badge coloré pour le status d'un finding.
const FindingStatusBadge = ({ status }) => {
  const meta = FINDING_STATUS_META[status] || FINDING_STATUS_META.open;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0 rounded text-[9px] font-semibold border ${meta.bg} ${meta.text} ${meta.border}`}>
      {meta.label}
    </span>
  );
};

// Dropdown kebab (⋮) pour piloter le status d'un finding.
const FindingActionMenu = ({ status, onChangeStatus, onAddNote, busy }) => {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const actions = [
    { value: 'contested', label: 'Marquer contesté',           hidden: status === 'contested' },
    { value: 'resolved',  label: 'Marquer résolu',             hidden: status === 'resolved'  },
    { value: 'refunded',  label: 'Marquer remboursé (€)',      hidden: status === 'refunded'  },
    { value: 'dismissed', label: 'Écarter',                    hidden: status === 'dismissed' },
    { value: 'open',      label: 'Rouvrir',                    hidden: status === 'open' || status === 'confirmed_by_invoice' },
  ];

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        disabled={busy}
        onClick={() => setOpen((v) => !v)}
        className="p-1 rounded hover:bg-gray-100 text-gray-500 disabled:opacity-40"
        title="Actions"
      >
        {busy
          ? <FiLoader className="h-3 w-3 animate-spin" />
          : <FiMoreVertical className="h-3 w-3" />}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-20 min-w-[180px] bg-white border border-gray-200 rounded shadow-lg py-1 text-[10px]">
          {actions.filter(a => !a.hidden).map((a) => (
            <button
              key={a.value}
              type="button"
              onClick={() => { setOpen(false); onChangeStatus(a.value); }}
              className="w-full text-left px-2 py-1 hover:bg-gray-50 text-gray-700"
            >
              {a.label}
            </button>
          ))}
          <div className="border-t border-gray-100 my-1" />
          <button
            type="button"
            onClick={() => { setOpen(false); onAddNote(); }}
            className="w-full text-left px-2 py-1 hover:bg-gray-50 text-gray-700 inline-flex items-center gap-1"
          >
            <FiEdit2 className="h-2.5 w-2.5" />
            Ajouter une note
          </button>
        </div>
      )}
    </div>
  );
};

// Modale pour saisir note + montant remboursé (transition vers `refunded`).
const FindingNoteModal = ({ mode, finding, onClose, onSubmit }) => {
  // mode = 'refunded' (note + montant) | 'note' (note seule)
  const [note, setNote] = useState(finding?.note || '');
  const [amount, setAmount] = useState(finding?.refunded_amount_eur || finding?.retroactif_eur || '');
  const [saving, setSaving] = useState(false);

  if (!mode) return null;

  const isRefund = mode === 'refunded';
  const title = isRefund ? 'Marquer comme remboursé' : 'Ajouter une note';

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const payload = { note };
      if (isRefund) {
        const n = Number(String(amount).replace(',', '.'));
        payload.refunded_amount_eur = Number.isFinite(n) ? n : null;
        payload.status = 'refunded';
      }
      await onSubmit(payload);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3">
      <div className="bg-white rounded shadow-xl border border-gray-200 w-full max-w-md">
        <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200">
          <h4 className="text-xs font-semibold text-gray-800">{title}</h4>
          <button onClick={onClose} className="p-0.5 rounded hover:bg-gray-100 text-gray-500">
            <FiX className="h-3 w-3" />
          </button>
        </div>
        <div className="p-3 space-y-2">
          {finding?.title && (
            <p className="text-[10px] text-gray-500 italic leading-snug">{finding.title}</p>
          )}
          {isRefund && (
            <label className="block">
              <span className="text-[10px] text-gray-500 uppercase tracking-wide">Montant remboursé (€)</span>
              <input
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="mt-1 w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-emerald-300"
                placeholder="0.00"
              />
            </label>
          )}
          <label className="block">
            <span className="text-[10px] text-gray-500 uppercase tracking-wide">Note</span>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              className="mt-1 w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-300 resize-none"
              placeholder="Commentaire interne (optionnel)…"
            />
          </label>
        </div>
        <div className="flex items-center justify-end gap-1.5 px-3 py-2 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-2 py-1 rounded text-[10px] text-gray-600 hover:bg-gray-100 disabled:opacity-40"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            className={`px-2 py-1 rounded text-[10px] font-semibold text-white inline-flex items-center gap-1 ${
              isRefund ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-blue-600 hover:bg-blue-700'
            } disabled:opacity-40`}
          >
            {saving && <FiLoader className="h-2.5 w-2.5 animate-spin" />}
            Valider
          </button>
        </div>
      </div>
    </div>
  );
};

const AuditFindingCard = ({ finding, session, onUpdated }) => {
  const [showPreuves, setShowPreuves] = useState(false);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [modalMode, setModalMode] = useState(null); // null | 'refunded' | 'note'
  const severity = SEVERITY_STYLES[finding.severity] || SEVERITY_STYLES.info;
  const SeverityIcon = severity.icon;
  const hasPreuves = finding.preuves && Object.keys(finding.preuves).length > 0;
  const findingId = finding.id;
  const status = finding.status || 'open';

  // PATCH status (transitions standards). Pour `refunded`, on passe par la modale (montant requis).
  const patchStatus = async (payload) => {
    if (!findingId) {
      toast.error('Finding non persisté — impossible de modifier le statut.');
      return null;
    }
    setBusy(true);
    try {
      const res = await fetch(`/api/enedis/audit/findings/${findingId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.message || `Erreur ${res.status}`);
      }
      const json = await res.json();
      const updated = json?.data || json;
      const meta = FINDING_STATUS_META[updated?.status || payload.status] || FINDING_STATUS_META.open;
      toast.success(`Statut mis à jour : ${meta.label}`);
      if (onUpdated) onUpdated(updated);
      return updated;
    } catch (e) {
      console.error('[AuditFindingCard] patch error:', e);
      toast.error(e.message || 'Erreur mise à jour statut');
      return null;
    } finally {
      setBusy(false);
    }
  };

  const handleChangeStatus = (newStatus) => {
    if (newStatus === 'refunded') {
      setModalMode('refunded');
    } else {
      patchStatus({ status: newStatus });
    }
  };

  const handleDownloadLetter = async () => {
    if (!findingId) {
      toast.error('Finding non persisté — courrier indisponible.');
      return;
    }
    setDownloading(true);
    try {
      const res = await fetch(`/api/enedis/audit/findings/${findingId}/letter`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.message || `Erreur ${res.status}`);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `courrier-reclamation-${finding.code || findingId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Courrier téléchargé');
    } catch (e) {
      console.error('[AuditFindingCard] letter error:', e);
      toast.error(e.message || 'Erreur téléchargement courrier');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <div className={`bg-white rounded border border-gray-200 border-l-2 ${severity.border} shadow-sm`}>
        <div className="p-2">
          <div className="flex items-start gap-2">
            <SeverityIcon className={`h-3.5 w-3.5 flex-shrink-0 mt-0.5 ${severity.iconColor}`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className={`text-[9px] font-semibold px-1.5 py-0 rounded ${severity.badge}`}>
                    {severity.label}
                  </span>
                  {finding.code && (
                    <span className="text-[9px] font-mono text-gray-400">{finding.code}</span>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <FindingStatusBadge status={status} />
                  <FindingActionMenu
                    status={status}
                    busy={busy}
                    onChangeStatus={handleChangeStatus}
                    onAddNote={() => setModalMode('note')}
                  />
                </div>
              </div>
              <h4 className="text-xs font-semibold text-gray-900 mt-1">{finding.title}</h4>
              {finding.description && (
                <p className="text-[11px] text-gray-600 mt-0.5 leading-snug">{finding.description}</p>
              )}

              {/* Bandeau impact */}
              {(finding.impact_annuel_eur != null || finding.retroactif_eur != null || finding.confirmed_amount_eur != null) && (
                <div className={`mt-1.5 ${severity.bg} rounded px-2 py-1 flex items-center gap-3 flex-wrap`}>
                  {finding.impact_annuel_eur != null && (
                    <span className="text-[10px] text-gray-700">
                      Impact <span className="font-bold">{formatEur(finding.impact_annuel_eur)}</span>/an
                    </span>
                  )}
                  {finding.retroactif_eur != null && (
                    <span className="text-[10px] text-gray-700">
                      Rétroactif 6 ans <span className="font-bold">{formatEur(finding.retroactif_eur)}</span>
                    </span>
                  )}
                  {finding.confirmed_amount_eur != null && Number(finding.confirmed_amount_eur) > 0 && (
                    <span className="text-[10px] text-blue-700">
                      Écart confirmé <span className="font-bold">{formatEur(finding.confirmed_amount_eur)}</span>/an
                    </span>
                  )}
                  {status === 'refunded' && finding.refunded_amount_eur != null && (
                    <span className="text-[10px] text-emerald-700">
                      Remboursé <span className="font-bold">{formatEur(finding.refunded_amount_eur)}</span>
                    </span>
                  )}
                </div>
              )}

              {/* Lien vers la facture qui a confirmé ce finding */}
              {finding.confirmed_by_invoice_id && (
                <div className="mt-1">
                  <button
                    type="button"
                    onClick={() => {
                      if (typeof window !== 'undefined') {
                        window.dispatchEvent(new CustomEvent('audit:scroll-to-invoice', {
                          detail: { invoiceId: finding.confirmed_by_invoice_id },
                        }));
                      }
                    }}
                    className="inline-flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    <FiCornerUpLeft className="h-2.5 w-2.5" />
                    Voir la facture qui a confirmé
                  </button>
                </div>
              )}

              {finding.action && (
                <div className="mt-1.5 flex items-start gap-1">
                  <FiTarget className="h-2.5 w-2.5 text-gray-400 mt-0.5 flex-shrink-0" />
                  <p className="text-[10px] text-gray-700 italic leading-snug">{finding.action}</p>
                </div>
              )}

              {finding.note && (
                <div className="mt-1.5 px-2 py-1 bg-yellow-50 border border-yellow-200 rounded">
                  <p className="text-[9px] text-yellow-700 uppercase tracking-wide font-semibold">Note</p>
                  <p className="text-[10px] text-gray-700 leading-snug whitespace-pre-line">{finding.note}</p>
                </div>
              )}

              {hasPreuves && (
                <div className="mt-1.5">
                  <button
                    type="button"
                    onClick={() => setShowPreuves((v) => !v)}
                    className="inline-flex items-center gap-1 text-[10px] text-gray-500 hover:text-gray-700"
                  >
                    {showPreuves ? <FiChevronUp className="h-2.5 w-2.5" /> : <FiChevronDown className="h-2.5 w-2.5" />}
                    Preuves ({Object.keys(finding.preuves).length})
                  </button>
                  {showPreuves && (
                    <pre className="mt-1 bg-gray-900 text-gray-100 rounded p-1.5 text-[9px] font-mono overflow-x-auto leading-tight">
{JSON.stringify(finding.preuves, null, 2)}
                    </pre>
                  )}
                </div>
              )}

              <div className="mt-2 flex justify-end">
                <button
                  type="button"
                  onClick={handleDownloadLetter}
                  disabled={!findingId || downloading}
                  title={!findingId ? 'Finding non persisté' : 'Télécharger le courrier de réclamation (PDF)'}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px] font-medium border border-blue-200 hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {downloading
                    ? <FiLoader className="h-2.5 w-2.5 animate-spin" />
                    : <FiDownload className="h-2.5 w-2.5" />}
                  Télécharger le courrier
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {modalMode && (
        <FindingNoteModal
          mode={modalMode}
          finding={finding}
          onClose={() => setModalMode(null)}
          onSubmit={async (payload) => { await patchStatus(payload); }}
        />
      )}
    </>
  );
};

// ─── Sprint 3 : factures fournisseur (upload + OCR + cross-check) ─────────────

const SUPPLIER_OPTIONS = [
  'EDF Pro',
  'Engie Pro',
  'TotalEnergies Pro',
  'OHM',
  'ekWateur',
  'Plüm',
  'Autre',
];

// Cache des listes de factures par PDL au scope module (persiste pendant la session JS).
// Évite un re-fetch au mount lorsqu'on revient sur l'onglet pour un même PDL.
const __invoicesCache = new Map(); // pdlId -> { invoices, ts }

const OCR_STATUS_META = {
  pending:    { label: 'En attente',   bg: 'bg-gray-100',    text: 'text-gray-600'  },
  processing: { label: 'OCR en cours', bg: 'bg-blue-100',    text: 'text-blue-700'  },
  completed:  { label: 'Terminé',      bg: 'bg-emerald-100', text: 'text-emerald-700' },
  failed:     { label: 'Échec',        bg: 'bg-red-100',     text: 'text-red-700'   },
};

const OcrStatusBadge = ({ status }) => {
  const meta = OCR_STATUS_META[status] || OCR_STATUS_META.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0 rounded text-[9px] font-semibold ${meta.bg} ${meta.text}`}>
      {meta.label}
    </span>
  );
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return iso;
  }
};

// Modale upload facture : drag & drop + champs optionnels + workflow polling OCR.
const InvoiceUploadModal = ({ pdlId, session, onClose, onCompleted }) => {
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [periodeDebut, setPeriodeDebut] = useState('');
  const [periodeFin, setPeriodeFin] = useState('');
  const [fournisseur, setFournisseur] = useState('');
  const [phase, setPhase] = useState('idle'); // idle | uploading | ocr | crosscheck | done | error
  const [errorMsg, setErrorMsg] = useState(null);
  const inputRef = React.useRef(null);

  const handlePickFile = (f) => {
    if (!f) return;
    if (f.type && f.type !== 'application/pdf' && !f.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Format non supporté — PDF uniquement');
      return;
    }
    setFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handlePickFile(f);
  };

  const pollOcrStatus = async (invoiceId) => {
    const maxAttempts = 20; // 20 * 3s = 60s
    let attempt = 0;
    while (attempt < maxAttempts) {
      await new Promise((r) => setTimeout(r, 3000));
      attempt += 1;
      const res = await fetch(`/api/enedis/audit/invoices/${invoiceId}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!res.ok) continue;
      const json = await res.json();
      const data = json?.data || json;
      const st = data?.ocr_status;
      if (st === 'completed') return data;
      if (st === 'failed') {
        throw new Error(data?.ocr_error || 'OCR a échoué côté backend');
      }
    }
    throw new Error('Timeout OCR — réessayez plus tard');
  };

  const handleSubmit = async () => {
    if (!file) {
      toast.error('Sélectionnez un PDF');
      return;
    }
    setPhase('uploading');
    setErrorMsg(null);
    try {
      const form = new FormData();
      form.append('invoice', file);
      if (periodeDebut) form.append('periode_debut', periodeDebut);
      if (periodeFin) form.append('periode_fin', periodeFin);
      if (fournisseur) form.append('fournisseur', fournisseur);

      const res = await fetch(`/api/enedis/audit/${pdlId}/invoice`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `Upload échoué (${res.status})`);
      }
      const json = await res.json();
      const invoiceId = json.invoice_id || json.data?.invoice_id;
      const initialStatus = json.ocr_status || json.data?.ocr_status;
      if (!invoiceId) throw new Error('invoice_id manquant dans la réponse backend');

      let finalData;
      if (initialStatus === 'completed') {
        // Déjà fini (rare) — fetch détail directement
        setPhase('crosscheck');
        const detailRes = await fetch(`/api/enedis/audit/invoices/${invoiceId}`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        finalData = (await detailRes.json())?.data || (await detailRes.json());
      } else {
        setPhase('ocr');
        finalData = await pollOcrStatus(invoiceId);
        setPhase('crosscheck');
        // Petit delai cosmétique pour laisser le user voir "cross-check"
        await new Promise((r) => setTimeout(r, 400));
      }

      setPhase('done');
      const impacted = Array.isArray(finalData?.findings_impacted) ? finalData.findings_impacted : [];
      const confirmedCount = impacted.filter((f) => f.status === 'confirmed_by_invoice').length;
      const dismissedCount = impacted.filter((f) => f.status === 'dismissed').length;
      const totalConfirmed = impacted
        .filter((f) => f.status === 'confirmed_by_invoice' && f.confirmed_amount_eur)
        .reduce((sum, f) => sum + Number(f.confirmed_amount_eur || 0), 0);

      if (impacted.length === 0) {
        toast.success('Facture analysée — aucun finding impacté');
      } else {
        const parts = [];
        if (confirmedCount > 0) {
          parts.push(`${confirmedCount} finding${confirmedCount > 1 ? 's' : ''} confirmé${confirmedCount > 1 ? 's' : ''}${totalConfirmed > 0 ? ` (${formatEur(totalConfirmed)}/an)` : ''}`);
        }
        if (dismissedCount > 0) {
          parts.push(`${dismissedCount} dismissed`);
        }
        toast.success(parts.join(', ') || 'Facture analysée');
      }

      if (onCompleted) onCompleted(finalData);
      onClose();
    } catch (e) {
      console.error('[InvoiceUploadModal] error:', e);
      setPhase('error');
      setErrorMsg(e.message || 'Erreur inconnue');
      toast.error(e.message || 'Erreur upload / OCR');
    }
  };

  const busy = phase !== 'idle' && phase !== 'error' && phase !== 'done';

  const phaseLabel = {
    uploading:  'Upload en cours…',
    ocr:        'OCR en cours (≈ 15 s)…',
    crosscheck: 'Cross-check des findings…',
    done:       'Terminé',
  }[phase];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3">
      <div className="bg-white rounded shadow-xl border border-gray-200 w-full max-w-lg">
        <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200">
          <h4 className="text-xs font-semibold text-gray-800 inline-flex items-center gap-1.5">
            <FiPaperclip className="h-3 w-3" />
            Joindre une facture fournisseur
          </h4>
          <button onClick={onClose} disabled={busy} className="p-0.5 rounded hover:bg-gray-100 text-gray-500 disabled:opacity-40">
            <FiX className="h-3 w-3" />
          </button>
        </div>

        <div className="p-3 space-y-2.5">
          {/* Drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => !busy && inputRef.current?.click()}
            className={`border-2 border-dashed rounded p-3 text-center cursor-pointer transition-colors ${
              dragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
            } ${busy ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            <input
              ref={inputRef}
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              onChange={(e) => handlePickFile(e.target.files?.[0])}
              disabled={busy}
            />
            {file ? (
              <div className="flex items-center justify-center gap-1.5 text-xs text-gray-700">
                <FiFile className="h-3.5 w-3.5 text-blue-600" />
                <span className="font-medium">{file.name}</span>
                <span className="text-[10px] text-gray-500">({Math.round(file.size / 1024)} ko)</span>
              </div>
            ) : (
              <>
                <FiUploadCloud className="h-6 w-6 text-gray-400 mx-auto mb-1" />
                <p className="text-xs text-gray-700">Glissez votre facture PDF ici</p>
                <p className="text-[10px] text-gray-500">ou cliquez pour parcourir</p>
              </>
            )}
          </div>

          {/* Champs optionnels */}
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[10px] text-gray-500 uppercase tracking-wide">Période début</span>
              <input
                type="date"
                value={periodeDebut}
                onChange={(e) => setPeriodeDebut(e.target.value)}
                disabled={busy}
                className="mt-1 w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-300 disabled:opacity-50"
              />
            </label>
            <label className="block">
              <span className="text-[10px] text-gray-500 uppercase tracking-wide">Période fin</span>
              <input
                type="date"
                value={periodeFin}
                onChange={(e) => setPeriodeFin(e.target.value)}
                disabled={busy}
                className="mt-1 w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-300 disabled:opacity-50"
              />
            </label>
          </div>

          <label className="block">
            <span className="text-[10px] text-gray-500 uppercase tracking-wide">Fournisseur</span>
            <select
              value={fournisseur}
              onChange={(e) => setFournisseur(e.target.value)}
              disabled={busy}
              className="mt-1 w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-300 disabled:opacity-50"
            >
              <option value="">— Non précisé —</option>
              {SUPPLIER_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>

          {/* Status pendant upload + OCR */}
          {busy && (
            <div className="rounded bg-blue-50 border border-blue-200 px-2 py-1.5 flex items-center gap-2">
              <FiLoader className="h-3 w-3 animate-spin text-blue-600" />
              <span className="text-[11px] text-blue-700 font-medium">{phaseLabel}</span>
            </div>
          )}

          {phase === 'error' && errorMsg && (
            <div className="rounded bg-red-50 border border-red-200 px-2 py-1.5 flex items-start gap-2">
              <FiXCircle className="h-3 w-3 text-red-600 mt-0.5 flex-shrink-0" />
              <span className="text-[11px] text-red-700 leading-snug">{errorMsg}</span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-1.5 px-3 py-2 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-2 py-1 rounded text-[10px] text-gray-600 hover:bg-gray-100 disabled:opacity-40"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={busy || !file}
            className="px-2 py-1 rounded text-[10px] font-semibold text-white bg-blue-600 hover:bg-blue-700 inline-flex items-center gap-1 disabled:opacity-40"
          >
            {busy && <FiLoader className="h-2.5 w-2.5 animate-spin" />}
            Lancer l'analyse OCR
          </button>
        </div>
      </div>
    </div>
  );
};

// Rend une table lisible à partir du JSON ocr_extracted (structure : composantes,
// energie, taxes, total — toléran les champs manquants).
const OcrExtractedTable = ({ ocrExtracted }) => {
  if (!ocrExtracted || typeof ocrExtracted !== 'object') {
    return <p className="text-[11px] text-gray-500 italic">Aucune donnée OCR disponible.</p>;
  }

  const sections = [
    { key: 'composantes', label: 'Composantes (TURPE)' },
    { key: 'energie',     label: 'Énergie' },
    { key: 'taxes',       label: 'Taxes' },
  ];

  const renderRows = (obj) => {
    if (!obj || typeof obj !== 'object') return null;
    return Object.entries(obj).map(([k, v]) => {
      // Si la valeur est un objet imbriqué simple { quantite, pu, montant } -> ligne compacte
      let display;
      if (v && typeof v === 'object' && !Array.isArray(v)) {
        const parts = [];
        if (v.quantite != null) parts.push(`Qté ${v.quantite}`);
        if (v.pu != null) parts.push(`PU ${v.pu}`);
        if (v.montant != null) parts.push(formatEur(v.montant));
        display = parts.length ? parts.join(' · ') : JSON.stringify(v);
      } else if (typeof v === 'number') {
        display = formatEur(v);
      } else {
        display = String(v ?? '—');
      }
      return (
        <tr key={k} className="border-b border-gray-100 last:border-0">
          <td className="py-1 px-2 text-[10px] text-gray-600">{k}</td>
          <td className="py-1 px-2 text-[10px] text-gray-900 font-mono text-right">{display}</td>
        </tr>
      );
    });
  };

  return (
    <div className="space-y-2">
      {sections.map(({ key, label }) => {
        const block = ocrExtracted[key];
        if (!block) return null;
        return (
          <div key={key}>
            <h5 className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold mb-0.5">{label}</h5>
            <table className="w-full text-left bg-gray-50 rounded overflow-hidden">
              <tbody>{renderRows(block)}</tbody>
            </table>
          </div>
        );
      })}
      {ocrExtracted.total != null && (
        <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded px-2 py-1.5">
          <span className="text-[11px] font-semibold text-blue-800">Total facture</span>
          <span className="text-[11px] font-bold text-blue-900">{formatEur(ocrExtracted.total)}</span>
        </div>
      )}
    </div>
  );
};

// Modale détail OCR d'une facture.
const InvoiceOcrDetailsModal = ({ invoice, onClose }) => {
  if (!invoice) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3">
      <div className="bg-white rounded shadow-xl border border-gray-200 w-full max-w-xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200">
          <h4 className="text-xs font-semibold text-gray-800 inline-flex items-center gap-1.5">
            <FiFile className="h-3 w-3" />
            Détail OCR — {invoice.fournisseur || 'Facture'}
          </h4>
          <button onClick={onClose} className="p-0.5 rounded hover:bg-gray-100 text-gray-500">
            <FiX className="h-3 w-3" />
          </button>
        </div>
        <div className="p-3 overflow-y-auto">
          <div className="grid grid-cols-2 gap-2 mb-2 text-[10px] text-gray-600">
            <div>
              <span className="text-gray-400 uppercase tracking-wide">Période</span>
              <div className="font-medium text-gray-800">
                {formatDate(invoice.periode_debut)} → {formatDate(invoice.periode_fin)}
              </div>
            </div>
            <div>
              <span className="text-gray-400 uppercase tracking-wide">Status OCR</span>
              <div><OcrStatusBadge status={invoice.ocr_status} /></div>
            </div>
          </div>
          <OcrExtractedTable ocrExtracted={invoice.ocr_extracted} />
        </div>
        <div className="flex items-center justify-end px-3 py-2 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-2 py-1 rounded text-[10px] text-gray-600 hover:bg-gray-100"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

// Section "Factures analysées" — liste collapsible des factures uploadées pour ce PDL.
const InvoicesSection = ({ session, invoices, loading }) => {
  const [open, setOpen] = useState(true);
  const [detailInvoice, setDetailInvoice] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [highlightId, setHighlightId] = useState(null);
  const containerRef = React.useRef(null);

  // Listener pour scroller vers une facture spécifique (déclenché depuis le lien
  // « Voir la facture qui a confirmé » sur un finding).
  useEffect(() => {
    const onScrollTo = (e) => {
      const id = e.detail?.invoiceId;
      if (!id) return;
      setOpen(true);
      setHighlightId(id);
      setTimeout(() => {
        const el = document.querySelector(`[data-invoice-id="${id}"]`);
        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 80);
      setTimeout(() => setHighlightId(null), 2400);
    };
    window.addEventListener('audit:scroll-to-invoice', onScrollTo);
    return () => window.removeEventListener('audit:scroll-to-invoice', onScrollTo);
  }, []);

  const openDetail = async (invoiceId) => {
    setLoadingDetail(true);
    try {
      const res = await fetch(`/api/enedis/audit/invoices/${invoiceId}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!res.ok) throw new Error(`Erreur ${res.status}`);
      const json = await res.json();
      setDetailInvoice(json?.data || json);
    } catch (e) {
      toast.error(e.message || 'Erreur chargement détail OCR');
    } finally {
      setLoadingDetail(false);
    }
  };

  return (
    <div ref={containerRef} className="bg-white rounded border border-gray-200">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-2 py-1.5 hover:bg-gray-50"
      >
        <div className="flex items-center gap-1.5">
          {open ? <FiChevronUp className="h-3 w-3 text-gray-500" /> : <FiChevronDown className="h-3 w-3 text-gray-500" />}
          <FiPaperclip className="h-3 w-3 text-gray-500" />
          <span className="text-xs font-semibold text-gray-800">Factures analysées</span>
          <span className="text-[10px] text-gray-500">({invoices.length})</span>
        </div>
        {loading && <FiLoader className="h-2.5 w-2.5 animate-spin text-gray-400" />}
      </button>

      {open && (
        <div className="border-t border-gray-100 p-2">
          {invoices.length === 0 ? (
            <p className="text-[11px] text-gray-500 italic py-2 text-center">
              Aucune facture analysée pour ce PDL. Cliquez sur « Joindre une facture » pour démarrer.
            </p>
          ) : (
            <div className="space-y-1.5">
              {invoices.map((inv) => (
                <div
                  key={inv.id}
                  data-invoice-id={inv.id}
                  className={`flex items-center justify-between gap-2 px-2 py-1.5 rounded border ${
                    highlightId === inv.id
                      ? 'border-blue-400 bg-blue-50 ring-1 ring-blue-300'
                      : 'border-gray-200 bg-gray-50'
                  } transition-colors`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[11px] font-semibold text-gray-800 truncate">
                        {inv.fournisseur || 'Fournisseur inconnu'}
                      </span>
                      <OcrStatusBadge status={inv.ocr_status} />
                      {inv.montant_total_eur != null && (
                        <span className="text-[10px] text-gray-700">
                          {formatEur(inv.montant_total_eur)}
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-gray-500 mt-0.5">
                      {formatDate(inv.periode_debut)} → {formatDate(inv.periode_fin)}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => openDetail(inv.id)}
                    disabled={inv.ocr_status !== 'completed' || loadingDetail}
                    className="px-2 py-0.5 rounded bg-white border border-gray-200 text-[10px] text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1 flex-shrink-0"
                  >
                    {loadingDetail ? <FiLoader className="h-2.5 w-2.5 animate-spin" /> : <FiEye className="h-2.5 w-2.5" />}
                    Voir le détail OCR
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {detailInvoice && (
        <InvoiceOcrDetailsModal invoice={detailInvoice} onClose={() => setDetailInvoice(null)} />
      )}
    </div>
  );
};

const AuditEnedisTab = ({ pdlId, year, session, onSummaryChange }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [auditData, setAuditData] = useState(null);
  // Sprint 3 : factures fournisseur uploadées
  const [invoices, setInvoices] = useState(() => {
    const cached = pdlId ? __invoicesCache.get(pdlId) : null;
    return cached?.invoices || [];
  });
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  // POST /persist : exécute l'audit + persiste les findings (chacun avec un id BDD).
  // Phase 2 : remplace l'ancien GET pour permettre les actions workflow ensuite.
  useEffect(() => {
    let cancelled = false;
    const persistAudit = async () => {
      if (!pdlId || !session?.access_token) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const url = `/api/enedis/audit/${pdlId}/persist`;
        const res = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify(year ? { year } : {}),
        });
        if (!res.ok) {
          if (cancelled) return;
          if (res.status === 404) {
            setError('not_available');
          } else {
            setError(`http_${res.status}`);
          }
          setAuditData(null);
          if (onSummaryChange) onSummaryChange(0);
          return;
        }
        const json = await res.json();
        if (cancelled) return;
        const data = json?.data || json;
        if (data && (Array.isArray(data.findings) || data.summary)) {
          setAuditData(data);
          if (onSummaryChange) onSummaryChange(data?.summary?.total_anomalies || 0);
          toast.success('Audit lancé et sauvegardé');
        } else {
          setError('invalid_response');
          setAuditData(null);
          if (onSummaryChange) onSummaryChange(0);
        }
      } catch (e) {
        if (cancelled) return;
        console.error('[AuditEnedisTab] persist error:', e);
        setError('network');
        setAuditData(null);
        if (onSummaryChange) onSummaryChange(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    persistAudit();
    return () => { cancelled = true; };
  }, [pdlId, year, session, onSummaryChange]);

  // Met à jour localement un finding après PATCH (status/note/montant).
  const handleFindingUpdated = useCallback((updated) => {
    if (!updated?.id) return;
    setAuditData((prev) => {
      if (!prev) return prev;
      const nextFindings = (prev.findings || []).map((f) =>
        f.id === updated.id ? { ...f, ...updated } : f
      );
      return { ...prev, findings: nextFindings };
    });
  }, []);

  // Fetch liste des factures uploadées pour ce PDL (avec cache scope-module).
  const fetchInvoices = useCallback(async ({ force = false } = {}) => {
    if (!pdlId || !session?.access_token) return;
    if (!force && __invoicesCache.has(pdlId)) {
      const cached = __invoicesCache.get(pdlId);
      setInvoices(cached.invoices);
      return;
    }
    setLoadingInvoices(true);
    try {
      const res = await fetch(`/api/enedis/audit/${pdlId}/invoices`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!res.ok) throw new Error(`Erreur ${res.status}`);
      const json = await res.json();
      const list = Array.isArray(json?.data) ? json.data : (Array.isArray(json) ? json : (json?.invoices || []));
      setInvoices(list);
      __invoicesCache.set(pdlId, { invoices: list, ts: Date.now() });
    } catch (e) {
      console.error('[AuditEnedisTab] fetchInvoices error:', e);
      // pas de toast bloquant : la section affichera "Aucune facture"
    } finally {
      setLoadingInvoices(false);
    }
  }, [pdlId, session]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // Callback après upload + OCR terminé : merge les findings impactés + refresh liste.
  const handleInvoiceCompleted = useCallback((invoiceData) => {
    if (!invoiceData) return;
    // Merge findings_impacted dans auditData
    const impacted = Array.isArray(invoiceData.findings_impacted) ? invoiceData.findings_impacted : [];
    if (impacted.length > 0) {
      setAuditData((prev) => {
        if (!prev) return prev;
        const byId = new Map(impacted.map((f) => [f.id, f]));
        const nextFindings = (prev.findings || []).map((f) =>
          byId.has(f.id) ? { ...f, ...byId.get(f.id) } : f
        );
        return { ...prev, findings: nextFindings };
      });
    }
    // Refresh forcé de la liste des factures (et invalide cache)
    __invoicesCache.delete(pdlId);
    fetchInvoices({ force: true });
  }, [pdlId, fetchInvoices]);

  // Loading
  if (loading) {
    return (
      <div className="max-h-[calc(100vh-180px)] overflow-y-auto p-2">
        <div className="bg-white rounded border border-gray-200 p-3 flex items-center gap-2">
          <FiLoader className="h-3.5 w-3.5 animate-spin text-blue-600" />
          <span className="text-xs text-gray-600">Audit ENEDIS en cours…</span>
        </div>
        <div className="space-y-1.5 mt-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="bg-white rounded border border-gray-200 border-l-2 border-l-gray-200 p-2 animate-pulse">
              <div className="h-2 w-1/3 bg-gray-200 rounded mb-1.5" />
              <div className="h-2 w-2/3 bg-gray-100 rounded mb-1" />
              <div className="h-2 w-1/2 bg-gray-100 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error / non disponible
  if (error || !auditData) {
    return (
      <div className="max-h-[calc(100vh-180px)] overflow-y-auto p-2">
        <div className="bg-gray-50 border border-gray-200 rounded p-4 text-center">
          <FiShield className="h-6 w-6 text-gray-400 mx-auto mb-2" />
          <p className="text-xs font-medium text-gray-700">Audit indisponible pour ce PDL</p>
          <p className="text-[10px] text-gray-500 mt-1">
            L'audit ENEDIS n'a pas pu être chargé. Réessayez plus tard ou contactez le support.
          </p>
        </div>
      </div>
    );
  }

  const summary = auditData.summary || {};
  const findings = Array.isArray(auditData.findings) ? auditData.findings : [];
  const totalAnomalies = summary.total_anomalies || 0;
  const totalWarnings = summary.total_warnings || 0;

  return (
    <div className="max-h-[calc(100vh-180px)] overflow-y-auto">
      <div className="p-2 space-y-2">
        {/* Header résumé */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white rounded p-2">
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <div className="flex items-center gap-2">
              <FiShield className="h-3.5 w-3.5" />
              <h3 className="text-xs font-bold">Audit ENEDIS</h3>
              {auditData.analysis_year && (
                <span className="text-[10px] bg-white/10 px-1.5 py-0 rounded">
                  Année {auditData.analysis_year}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => setUploadModalOpen(true)}
              className="inline-flex items-center gap-1 px-2 py-1 rounded bg-white/15 hover:bg-white/25 border border-white/20 text-[10px] font-medium transition-colors"
              title="Joindre une facture fournisseur pour confirmer les findings"
            >
              <FiPaperclip className="h-2.5 w-2.5" />
              Joindre une facture
            </button>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-1.5">
            <div className="bg-white/10 rounded px-2 py-1">
              <div className="text-[9px] uppercase opacity-70">Anomalies</div>
              <div className="text-xs font-bold text-red-300">{totalAnomalies}</div>
            </div>
            <div className="bg-white/10 rounded px-2 py-1">
              <div className="text-[9px] uppercase opacity-70">Avertissements</div>
              <div className="text-xs font-bold text-amber-300">{totalWarnings}</div>
            </div>
            <div className="bg-white/10 rounded px-2 py-1">
              <div className="text-[9px] uppercase opacity-70">Risque annuel</div>
              <div className="text-xs font-bold">{formatEur(summary.risque_annuel_eur)}<span className="text-[9px] opacity-70">/an</span></div>
            </div>
            <div className="bg-white/10 rounded px-2 py-1">
              <div className="text-[9px] uppercase opacity-70">Rétroactif 6 ans</div>
              <div className="text-xs font-bold">~ {formatEur(summary.retroactif_6ans_eur)}</div>
            </div>
          </div>
        </div>

        {/* Liste findings */}
        {findings.length === 0 ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded p-3 flex items-start gap-2">
            <FiCheckCircle className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-emerald-800">Aucune anomalie détectée</p>
              <p className="text-[11px] text-emerald-700 mt-0.5 leading-snug">
                Votre facturation TURPE semble cohérente avec les données ENEDIS.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-1.5">
            {findings.map((f, idx) => (
              <AuditFindingCard
                key={f.id || (f.code ? `${f.code}-${idx}` : idx)}
                finding={f}
                session={session}
                onUpdated={handleFindingUpdated}
              />
            ))}
          </div>
        )}

        {/* Section "Factures analysées" */}
        <InvoicesSection
          session={session}
          invoices={invoices}
          loading={loadingInvoices}
        />
      </div>

      {/* Modale upload facture */}
      {uploadModalOpen && (
        <InvoiceUploadModal
          pdlId={pdlId}
          session={session}
          onClose={() => setUploadModalOpen(false)}
          onCompleted={handleInvoiceCompleted}
        />
      )}
    </div>
  );
};

// ===== COMPOSANT PRINCIPAL REDESIGNÉ =====
const AnalyseTurpe = () => {
  const { session } = useOutletContext();
  const { pdlId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  // Année par défaut = dernière année complète (engine TURPE exige >=330 jours)
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear() - 1);
  const availableYears = [2023, 2024, 2025, 2026, 2027]; 
  const [analysisData, setAnalysisData] = useState(null);
  const [allAnalyses, setAllAnalyses] = useState([]);
  const [clientsWithPdls, setClientsWithPdls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('synthese');
  const [relanceEnCours, setRelanceEnCours] = useState(false);
  const [activeStrategicTab, setActiveStrategicTab] = useState('load-shifting');
  const [auditAnomaliesCount, setAuditAnomaliesCount] = useState(0);
  const [clientSearch, setClientSearch] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);

const clientDataForList = useMemo(() => {
  // Si la liste des clients n'est pas encore chargée, on retourne un tableau vide.
  if (!clientsWithPdls || clientsWithPdls.length === 0) return [];

  // On transforme la liste des clients pour y ajouter des informations utiles.
  return clientsWithPdls.map(client => {
    const clientPdls = client.pdl_statuses || [];
    let analyzedPdlCount = 0;

    // Pour chaque PDL du client, on vérifie s'il a une analyse correspondante.
    const pdlsWithStatus = clientPdls.map(pdl => {
      const hasAnalysis = allAnalyses.some(analysis => analysis.pdl_id === pdl.pdl_id);
      if (hasAnalysis) {
        analyzedPdlCount++;
      }
      return {
        ...pdl,
        hasAnalysis: hasAnalysis,
      };
    });

    // On retourne un nouvel objet "client" enrichi.
    return {
      ...client,
      pdls: pdlsWithStatus, // La liste de ses PDLs avec le statut d'analyse
      analyzedPdlCount: analyzedPdlCount, // Le nombre de PDLs analysés
      totalPdlCount: clientPdls.length, // Le nombre total de PDLs
    };
  });
}, [clientsWithPdls, allAnalyses]);

const filteredClients = useMemo(() => {
    // Si la recherche est vide, on retourne la liste complète (ou une partie pour la performance)
    if (!clientSearch) {
      return clientDataForList;
    }
    
    const lowercasedFilter = clientSearch.toLowerCase();
    
    return clientDataForList.filter(client => {
      // On cherche dans le nom de la personne
      const nameMatch = client.user_name && client.user_name.toLowerCase().includes(lowercasedFilter);
      // On cherche dans le nom de la société
      const companyMatch = client.company_name && client.company_name.toLowerCase().includes(lowercasedFilter);
      
      return nameMatch || companyMatch;
    });
  }, [clientSearch, clientDataForList]);

  // Configuration des onglets
  const tabs = [
    { 
      id: 'synthese', 
      label: 'Synthèse', 
      icon: FiPieChart
    },
    { 
      id: 'courbe', 
      label: 'Courbe de charge', 
      icon: FiBarChart2
    },
    { 
      id: 'depassements', 
      label: 'Dépassements', 
      icon: FiAlertTriangle,
      badge: analysisData?.analysisResults?.turpe6_details?.depassements_detectes?.length || null
    },
    { 
      id: 'optimisation', 
      label: 'Optimisation Contractuelle', 
      icon: FiTool
    },
    {
    id: 'strategique',
    label: 'Optimisation Stratégique',
    icon: FiZap,
    badge: '2' // Indique qu'il y a maintenant 2 analyses disponibles
  },
  {
    id: 'audit',
    label: 'Audit ENEDIS',
    icon: FiShield,
    badge: auditAnomaliesCount > 0 ? String(auditAnomaliesCount) : null,
  }
  ];

  const loadOptimizationData = useCallback(async (id) => { // Il n'a plus besoin de `existingData`
    if (!session?.access_token) return null; // On retourne null s'il n'y a pas de session
  
    try {
      const summaryResponse = await fetch(`/api/turpe/get-analysis-summary/${id}`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      const summaryResult = await summaryResponse.json();

      if (summaryResult.success && summaryResult.data) {
        return summaryResult.data; // On retourne les données brutes de la BDD
      }

      return null; // On retourne null si rien n'est trouvé

    } catch (error) {
      console.error("Erreur chargement optimisations:", error);
      return null; // On retourne null en cas d'erreur
    }
}, [session]);

const loadAnalysisData = useCallback(async (id, yearToFetch) => {
    if (!session?.access_token) {
        setLoading(false);
        return;
    }
    // Defaults : si pas d'année passée (cas du bouton "Rafraîchir" en haut
    // à droite de la page détail qui appelle `loadAnalysisData(pdlId)` sans
    // year), on retombe sur l'année courante − 1 (l'engine TURPE refuse
    // les analyses < 330 jours, donc l'année courante n'est jamais valide).
    const yearNumeric = Number(yearToFetch);
    const yearSafe = Number.isFinite(yearNumeric) && yearNumeric > 0
      ? yearNumeric
      : (new Date().getFullYear() - 1);
    setLoading(true);

    try {
        // Pour éviter la répétition, on définit les en-têtes une seule fois
        const authHeaders = {
            'Authorization': `Bearer ${session.access_token}`
        };

        const [analysisResult, pdlResult, optimizationResult] = await Promise.all([
            fetch(`/api/turpe/get-analysis/${id}?year=${yearSafe}`, { headers: authHeaders }).then(res => res.json()),
            fetch(`/api/pdl/${id}`, { headers: authHeaders }).then(res => res.json()),
            loadOptimizationData(id)
        ]);

        // On vérifie que les deux appels principaux ont réussi avant de continuer
        if (analysisResult.success && pdlResult.success && analysisResult.data) {
            const finalData = {
                pdl: pdlResult.data, // On peut maintenant utiliser pdlResult.data directement
                year: analysisResult.data.analysis_year,
                analysisResults: {
                    ...analysisResult.data.analysis_results,
                    turpe6_details: {
                        ...analysisResult.data.analysis_results.turpe6_details,
                        economie_potentielle_max: optimizationResult?.best_saving_eur || 0,
                        version_of_use_saving: economieVersionDuResume(optimizationResult),
                        recommended_version: optimizationResult?.recommended_version_of_use || null
                    }
                },
                // Summary complet (chosen_optimization, chosen_version_of_use,
                // version_of_use_details_json, etc.) requis par CompactKPICards
                // pour calculer l'éco totale en cohérence avec le choix final.
                analysisSummary: optimizationResult || null,
            };
            
            setAnalysisData(finalData);

        } else {
            // Gérer le cas où l'une des requêtes échoue
            if (!analysisResult.success) {
                 toast.error("Impossible de charger les données d'analyse TURPE.");
            }
             if (!pdlResult.success) {
                 toast.error("Impossible de charger les informations du PDL.");
            }
            setAnalysisData(null);
        }
    } catch (error) {
        console.error('Erreur chargement analyse PDL:', error);
        // L'erreur est souvent une SyntaxError si une des réponses n'est pas du JSON (suite à 401/404)
        toast.error('Une erreur technique est survenue lors du chargement.');
    } finally {
        setLoading(false);
    }
}, [session, loadOptimizationData]);

  const loadGlobalData = useCallback(async () => {
    if (!session?.access_token) {
      setLoading(false);
      return;
    }
    setLoading(true); 
    try {
      // On passe l'année en paramètre aux deux appels API
      const [analysesResponse, clientsResponse] = await Promise.all([
        fetch(`/api/turpe/get-all-analyses?year=${selectedYear}`, {
          headers: { 'Authorization': `Bearer ${session.access_token}` }
        }),
        fetch(`/api/admin/clients-with-pdls?year=${selectedYear}`, {
          headers: { 'Authorization': `Bearer ${session.access_token}` }
        })
      ]);
      
      const analysesResult = await analysesResponse.json();
      const clientsResult = await clientsResponse.json();
      
      if (analysesResult.success) setAllAnalyses(analysesResult.data || []);
      if (clientsResult.success) setClientsWithPdls(clientsResult.data || []);
      
    } catch (error) {
      console.error('Erreur chargement données globales:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
}, [session, selectedYear]); 

  const handleAnalysisUpdate = (updatedValues) => {
  setAnalysisData(prevData => {
    if (!prevData?.analysisResults?.turpe6_details) {
      console.warn("Mise à jour ignorée car la structure de données initiale est incomplète.");
      return prevData;
    }

    const newAnalysisData = {
      ...prevData,
      analysisResults: {
        ...prevData.analysisResults,
        turpe6_details: {
          ...prevData.analysisResults.turpe6_details,
          // ✅ CORRECTION : On met à jour les deux champs pour être sûr
          economie_potentielle_max: updatedValues.best_saving_eur,
          economie_potentielle: updatedValues.best_saving_eur, // Fallback
        },
      },
      // Propager TOUT le summary (chosen_optimization, manual_saving_eur,
      // version_of_use_saving_eur, version_of_use_details_json, etc.) à
      // CompactKPICards via analysisData.analysisSummary, pour que le KPI
      // "Économie potentielle" reflète le choix retenu et pas le best auto.
      analysisSummary: {
        ...(prevData.analysisSummary || {}),
        ...updatedValues,
      },
    };

      return newAnalysisData;
    });
  };

  useEffect(() => {
    if (pdlId) {
        const yearFromState = location.state?.year || selectedYear;
        if (!analysisData || analysisData.pdl?.id !== pdlId || analysisData.year !== yearFromState) {
            loadAnalysisData(pdlId, yearFromState);
        }
    }
    // Vue globale : la nouvelle TurpeGlobalView gère son propre fetch
    // (useOptimizablePDLs). On ne déclenche plus loadGlobalData ici car
    // l'endpoint /api/turpe/get-all-analyses est cassé (relation
    // turpe_analyses↔pdl_id absente du schema cache) et son résultat
    // n'est pas consommé par TurpeGlobalView.
}, [pdlId, location.state, selectedYear, loadAnalysisData, analysisData]);

  // Verdict lu dans le résumé partagé (turpe_analyses.analysis_results.conclusion de l'année,
  // écrit par le calcul de base). Non concluant / non vérifié → page bloquée.
  const motifPage = motifBlocageDe(analysisData?.analysisSummary);
  const relancerAnalyse = async () => {
    const annee = analysisData?.year || location.state?.year || selectedYear;
    setRelanceEnCours(true);
    const toastId = toast.loading('Relance complète de l\'analyse…');
    try {
      const { avertissements } = await relancerAnalyseTurpe({
        pdlId, annee, session, onEtape: (texte) => toast.loading(texte, { id: toastId }),
      });
      toast.success('Analyse relancée : résultats et choix final antérieurs effacés.', { id: toastId });
      avertissements.forEach((m) => toast(m, { icon: '⚠️', duration: 12000 }));
      await loadAnalysisData(pdlId, annee);
    } catch (e) {
      console.error('[AnalyseTurpe] relance', e);
      toast.error('Relance impossible : ' + (e.message || 'inconnue'), { id: toastId });
    } finally {
      setRelanceEnCours(false);
    }
  };
  const PanneauBloque = () => (
    <div className="p-3 m-2 border border-amber-200 bg-amber-50 rounded text-[11px] text-amber-900" data-testid="turpe-onglet-bloque">
      <p className="font-semibold">Aucun gain ni recommandation tant que l'analyse n'est pas concluante.</p>
      <p className="mt-0.5">{motifPage}</p>
    </div>
  );

  const renderTabContent = () => {
  switch (activeTab) {
    case 'synthese':
      return <SyntheseTab results={analysisData?.analysisResults} pdlData={analysisData?.pdl} year={analysisData?.year} />;
    case 'courbe':
      return <CourbeChargeTab results={analysisData?.analysisResults} pdlId={pdlId} year={analysisData?.year} session={session} />;
    case 'depassements':
      return <DepassementsTab results={analysisData?.analysisResults} pdlData={analysisData?.pdl} />;
    case 'optimisation':

      return (
        <OptimisationTab 
          results={analysisData?.analysisResults} 
          pdlData={analysisData?.pdl}
          session={session}
          analysisYear={analysisData?.year} 
          onAnalysisUpdated={handleAnalysisUpdate} 
          blocage={motifPage}
          onRelancer={relancerAnalyse}
        />
      );
    case 'audit':
      return (
        <AuditEnedisTab
          pdlId={pdlId}
          year={analysisData?.year}
          session={session}
          onSummaryChange={setAuditAnomaliesCount}
        />
      );
    case 'strategique':
      if (motifPage) return <PanneauBloque />;
      return (
        <div className="max-h-[calc(100vh-180px)] overflow-y-auto">
          <div className="p-2 space-y-2">
            {/* Système d'onglets pour les différentes optimisations */}
            <div className="bg-white rounded border border-gray-200">
              <div className="border-b border-gray-200">
                <nav className="flex gap-1 px-2" aria-label="Optimisations">
                  {[
                    {
                      id: 'load-shifting',
                      label: 'Peak Shaving',
                      icon: '📊',
                      description: 'Déplacement de charge'
                    },
                    {
                      id: 'reactive-energy',
                      label: 'Énergie Réactive',
                      icon: '🔋',
                      description: 'Compensation réactive'
                    }
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveStrategicTab(tab.id)}
                      className={`${
                        activeStrategicTab === tab.id
                          ? 'border-blue-500 text-blue-600 bg-blue-50'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      } group inline-flex items-center py-1 px-2 border-b-2 font-medium text-[11px] transition-all duration-200`}
                    >
                      <span className="text-xs mr-1">{tab.icon}</span>
                      <span>{tab.label}</span>
                      <span className="ml-1 text-[9px] opacity-75">· {tab.description}</span>
                    </button>
                  ))}
                </nav>
              </div>

              <div className="p-2">
                {activeStrategicTab === 'load-shifting' && (
                  <LoadShiftingAnalysis
                    pdlData={analysisData?.pdl}
                    session={session}
                  />
                )}
                {activeStrategicTab === 'reactive-energy' && (
                  <ReactiveEnergyAnalysis
                    pdlData={analysisData?.pdl}
                    session={session}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      );
    default:
      return <SyntheseTab results={analysisData?.analysisResults} pdlData={analysisData?.pdl} year={analysisData?.year} />;
  }
};

  // Loading global réservé à la vue détail (pdlId). La vue globale
  // (TurpeGlobalView) gère son propre état de chargement via useOptimizablePDLs.
  if (loading && pdlId) {
    return (
      <div className="h-screen flex justify-center items-center">
        <div className="text-center space-y-4">
          <FiLoader className="animate-spin h-12 w-12 text-blue-600 mx-auto" />
          <p className="text-gray-600 font-medium">Chargement des analyses...</p>
        </div>
      </div>
    );
  }

  // Si on est sur un PDL spécifique
  if (pdlId) {
    if (!analysisData?.analysisResults) {
      // Lance le calcul TURPE puis recharge la page (équivalent
      // handleLaunchAnalysis d'AcheminementDetail).
      const triggerAnalysis = async () => {
        const yearToAnalyze = location.state?.year || selectedYear;
        const toastId = toast.loading('Étape 1/3 : récupération du PDL…');
        try {
          const { avertissements } = await relancerAnalyseTurpe({
            pdlId, annee: yearToAnalyze, session,
            onEtape: (texte) => toast.loading(texte, { id: toastId }),
          });
          toast.success('Analyse terminée !', { id: toastId });
          avertissements.forEach(message => toast(message, { icon: '⚠️', duration: 12000 }));
          loadAnalysisData(pdlId, yearToAnalyze);
        } catch (e) {
          console.error('[AnalyseTurpe] triggerAnalysis error:', e);
          toast.error('Erreur : ' + (e.message || 'inconnue'), { id: toastId });
        }
      };

      return (
        <div className="h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center bg-white rounded-2xl p-12 shadow-lg max-w-md">
            <FiBarChart2 className="h-20 w-20 text-gray-400 mx-auto mb-6" />
            <h3 className="text-xs font-bold text-gray-900 mb-4">Aucune analyse disponible</h3>
            <p className="text-gray-600 mb-6">
              Aucune analyse TURPE n'a encore été effectuée pour ce PDL. Lance l'analyse maintenant pour calculer le potentiel d'optimisation.
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={triggerAnalysis}
                className="inline-flex items-center px-6 py-3 bg-emerald-600 text-white font-semibold rounded-xl hover:bg-emerald-700 transition-colors"
              >
                <FiPlay className="h-5 w-5 mr-2" />
                Lancer l'analyse
              </button>
              <Link
                to="/dashboard/admin/analyse-turpe"
                className="inline-flex items-center px-6 py-3 bg-white text-gray-700 font-semibold rounded-xl border border-gray-300 hover:bg-gray-50 transition-colors"
              >
                <FiArrowLeft className="h-5 w-5 mr-2" />
                Retour
              </Link>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="h-screen flex flex-col bg-gray-50">
        {/* Header compact */}
        <CompactHeader 
          pdlData={analysisData?.pdl}
          analysisData={analysisData}
          loading={loading}
          onRefresh={() => loadAnalysisData(pdlId, analysisData?.year)}
          analysisYear={analysisData?.year}
          motifBlocage={motifPage}
        />

        {/* KPI Cards */}
        <BandeauConclusionTurpe resume={analysisData?.analysisSummary?.resume} className="mb-2" onRelancer={relancerAnalyse} enCours={relanceEnCours} />
        <CompactKPICards results={analysisData?.analysisResults} analysisSummary={analysisData?.analysisSummary} />

        {/* Système d'onglets */}
        <TabSystem 
          activeTab={activeTab}
          onTabChange={setActiveTab}
          tabs={tabs}
        />

        {/* Contenu des onglets */}
        <div className="flex-1 overflow-hidden bg-white">
          {renderTabContent()}
        </div>
      </div>
    );
  }

  // Vue globale — format table aligné AnalyseCspe
  return <TurpeGlobalView session={session} navigate={navigate} />;
};

export default AnalyseTurpe;
